import React, { useState, useEffect, useMemo } from 'react';
import { UserRole } from '../types';
import {
  AlertTriangle,
  Clock,
  CircleDollarSign,
  Users,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  FileEdit,
  ExternalLink,
  Mail,
  X,
  FileCheck2,
  Stethoscope,
  Activity,
  TrendingUp,
  FileSpreadsheet,
  UploadCloud,
  Layers,
  CreditCard,
  Calendar,
  ArrowUpRight,
  ShieldAlert,
  Zap
} from 'lucide-react';
import { IconBadge } from './IconBadge';
import { playPulseSearchBeacon } from '../utils/pulseAudio';
import { BillingHoldResolvePanel } from './BillingHoldResolvePanel';

interface DashboardViewProps {
  stats: {
    activeClients: number;
    pendingSignatures: number;
    unbilledRevenue: number;
    criticalQueue: any[];
    expiringAuths: any[];
    expiringPocs?: any[];
    systemStatus: any;
    wellSkyLastSync?: string | null;
  } | null;
  userRole: UserRole;
  onNavigateTab: (tab: string, subTab?: string) => void;
  onOpenAIWithPrompt: (prompt: string) => void;
  onSelectClient: (clientId: string) => void;
  onSelectService: (serviceId: string) => void;
  onOpenAuthEmail?: (clientId: string, templateKey: 'auth_7day' | 'auth_3day', authContext?: any) => void;
  onRefreshStats?: () => void;
}

export const DashboardView: React.FC<DashboardViewProps> = ({
  stats,
  userRole,
  onNavigateTab,
  onOpenAIWithPrompt,
  onSelectClient,
  onSelectService,
  onOpenAuthEmail,
  onRefreshStats
}) => {
  const isClinician = userRole === 'ASSISTANT' || userRole === 'SUPERVISOR';
  const isReferralCoordinator = userRole === 'REFERRAL_COORDINATOR' || userRole === 'OFFICE';
  const activeRole = userRole;
  const isOwner = activeRole === 'OWNER';
  const [resolveServiceId, setResolveServiceId] = useState<string | null>(null);

  // POC window logic:
  // Referral Coordinator uses a 30-day window (all POCs with daysRemaining <= 30 + expired + conflict + missing)
  // Owner uses a 14-day window (all POCs with daysRemaining <= 14 + expired + conflict + missing), preserving existing Owner dashboard logic
  // Canonical client status must be ACTIVE: INACTIVE clients strictly excluded.
  const rcExpiringPocs = useMemo(() => {
    return (stats?.expiringPocs || []).filter((poc: any) => {
      if (poc.client_status !== 'ACTIVE') return false;
      if (poc.isMissing || poc.isConflict || poc.poc_status === 'CONFLICT_NEEDS_REVIEW' || poc.poc_status === 'MISSING' || poc.poc_status === 'EXPIRED') {
        return true;
      }
      const days = typeof poc.daysRemaining === 'number' ? poc.daysRemaining : 999;
      if (days < 0) return true;
      return days <= 30;
    });
  }, [stats?.expiringPocs]);

  const ownerExpiringPocs = useMemo(() => {
    return (stats?.expiringPocs || []).filter((poc: any) => {
      if (poc.client_status !== 'ACTIVE') return false;
      if (poc.isMissing || poc.isConflict || poc.poc_status === 'CONFLICT_NEEDS_REVIEW' || poc.poc_status === 'MISSING' || poc.poc_status === 'EXPIRED') {
        return true;
      }
      const days = typeof poc.daysRemaining === 'number' ? poc.daysRemaining : 999;
      if (days < 0) return true;
      return days <= 14;
    });
  }, [stats?.expiringPocs]);

  const activeExpiringAuths = useMemo(() => {
    return (stats?.expiringAuths || []).filter((a: any) => a.client_status === 'ACTIVE');
  }, [stats?.expiringAuths]);
  const [liveWellSkySync, setLiveWellSkySync] = useState<string | null>(null);
  const [selectedAuthHistory, setSelectedAuthHistory] = useState<{ auth: any; noticeType?: '7_DAY' | '3_DAY' } | null>(null);
  const [updatingNoticeId, setUpdatingNoticeId] = useState<string | null>(null);
  const [expandedHoldServiceIds, setExpandedHoldServiceIds] = useState<Set<string>>(new Set());

  const toggleHoldExpand = (serviceId: string) => {
    setExpandedHoldServiceIds((prev) => {
      const next = new Set(prev);
      if (next.has(serviceId)) {
        next.delete(serviceId);
      } else {
        next.add(serviceId);
      }
      return next;
    });
  };

  const handleUpdateNoticeStatus = async (noticeId: string, newStatus: 'BOUNCED' | 'FAILED' | 'ACCEPTED' | 'SENT', reason?: string) => {
    setUpdatingNoticeId(noticeId);
    try {
      const res = await fetch(`/api/authorizations/notices/${noticeId}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: newStatus,
          reason: reason || `Manual coordinator reconciliation: marked as ${newStatus}`
        })
      });
      if (res.ok) {
        // Update local modal state immediately
        setSelectedAuthHistory((prev) => {
          if (!prev) return null;
          const updatedHistory = (prev.auth.notice_history || []).map((n: any) =>
            n.id === noticeId ? { ...n, delivery_status: newStatus, delivery_result: reason || `Marked as ${newStatus}` } : n
          );
          return {
            ...prev,
            auth: {
              ...prev.auth,
              notice_history: updatedHistory
            }
          };
        });
      }
    } catch (err) {
      console.error('Failed to update notice status:', err);
    } finally {
      setUpdatingNoticeId(null);
    }
  };

  const formatNoticeTimestamp = (dateStr?: string | null): string => {
    if (!dateStr) return '';
    try {
      let clean = dateStr.trim();
      if (!clean.endsWith('Z') && !clean.includes('+') && !clean.includes('T')) {
        clean = clean.replace(' ', 'T') + 'Z';
      }
      const date = new Date(clean);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleString('en-US', {
        timeZone: 'America/Phoenix',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return dateStr || '';
    }
  };

  const formatDisplayName = (item: { client_name?: string; first_name?: string; last_name?: string }) => {
    if (item.client_name) return item.client_name;
    const fn = (item.first_name || '').trim();
    const ln = (item.last_name || '').trim();
    if (fn && ln) {
      return `${fn} ${ln}`;
    }
    return fn || ln || 'Client';
  };

  const formatPocReminderName = (item: { client_name?: string; first_name?: string; last_name?: string; poc_stream?: string | null }) => {
    const name = formatDisplayName(item);
    if (item.poc_stream === 'SPEECH') return `${name} · Speech`;
    if (item.poc_stream === 'FEEDING') return `${name} · Feeding`;
    return name;
  };

  const formatDateMMDDYY = (dateStr?: string | null): string => {
    if (!dateStr) return 'N/A';
    const trimmed = dateStr.trim();
    if (!trimmed || trimmed === 'N/A') return 'N/A';

    // Check if format is already MM/DD/YY
    const mmddyyRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{2})$/;
    const mMatch = trimmed.match(mmddyyRegex);
    if (mMatch) {
      const month = mMatch[1].padStart(2, '0');
      const day = mMatch[2].padStart(2, '0');
      return `${month}/${day}/${mMatch[3]}`;
    }

    // Check if format is MM/DD/YYYY
    const mmddyyyyRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const myMatch = trimmed.match(mmddyyyyRegex);
    if (myMatch) {
      const month = myMatch[1].padStart(2, '0');
      const day = myMatch[2].padStart(2, '0');
      const year = myMatch[3].slice(-2);
      return `${month}/${day}/${year}`;
    }

    // Check if format is YYYY-MM-DD
    const yyyymmddRegex = /^(\d{4})-(\d{2})-(\d{2})/;
    const ymMatch = trimmed.match(yyyymmddRegex);
    if (ymMatch) {
      const year = ymMatch[1].slice(-2);
      const month = ymMatch[2];
      const day = ymMatch[3];
      return `${month}/${day}/${year}`;
    }

    // Fallback date parsing
    try {
      let clean = trimmed;
      if (!clean.endsWith('Z') && !clean.includes('+') && !clean.includes('T')) {
        clean = clean.replace(' ', 'T') + 'Z';
      }
      const d = new Date(clean);
      if (isNaN(d.getTime())) return trimmed;
      const month = String(d.getUTCMonth() + 1).padStart(2, '0');
      const day = String(d.getUTCDate()).padStart(2, '0');
      const year = String(d.getUTCFullYear()).slice(-2);
      return `${month}/${day}/${year}`;
    } catch {
      return trimmed;
    }
  };

  const getPocExpirationDates = (poc: any): string[] => {
    if (poc.isMissing) return [];
    const rawVal = poc.poc_end_dates || poc.poc_end_date;
    if (!rawVal) return [];

    let rawList: string[] = [];
    if (Array.isArray(rawVal)) {
      rawList = rawVal.map((v: any) => String(v).trim()).filter(Boolean);
    } else if (typeof rawVal === 'string') {
      rawList = rawVal
        .split(/[\n;|,]+|\bvs\b/i)
        .map((p: string) => p.trim())
        .filter((p: string) => p && p !== '—' && p.toUpperCase() !== 'N/A');
    }

    if (rawList.length === 0) return [];
    return rawList.map((part: string) => formatDateMMDDYY(part));
  };

  useEffect(() => {
    fetch('/api/wellsky-sync/status')
      .then((res) => res.json())
      .then((data) => {
        if (data?.lastSuccessfulRetrieval) {
          setLiveWellSkySync(data.lastSuccessfulRetrieval);
        }
      })
      .catch(() => {});
  }, []);

  const rawWellSkyTime = stats?.wellSkyLastSync || stats?.systemStatus?.wellSkyLastSync || liveWellSkySync || null;
  const formatSyncTime = (ts: string | null | undefined) => {
    if (!ts) return 'No import yet';
    try {
      let cleanTs = ts.trim();
      if (!cleanTs.endsWith('Z') && !cleanTs.includes('+') && !cleanTs.includes('T')) {
        cleanTs = cleanTs.replace(' ', 'T') + 'Z';
      } else if (cleanTs.includes('T') && !cleanTs.endsWith('Z') && !cleanTs.includes('+')) {
        cleanTs = cleanTs + 'Z';
      }
      const d = new Date(cleanTs);
      if (isNaN(d.getTime())) return ts;
      return d.toLocaleString('en-US', {
        timeZone: 'America/Phoenix',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return ts;
    }
  };

  // Helper for Authorization expiration styling:
  // 14–8 days: normal attention styling
  // 7–4 days: AMBER
  // 3–0 days: RED
  // already expired: RED
  const getAuthBadge = (auth: any) => {
    const days = typeof auth.daysRemaining === 'number' ? auth.daysRemaining : 999;
    const isExhausted = auth.isExhausted || (auth.total_units > 0 && auth.units_used >= auth.total_units);

    if (days < 0) {
      return {
        badge: `EXPIRED (${Math.abs(days)}d AGO)`,
        badgeClass: 'bg-rose-500/20 text-rose-300 border-rose-500/40 shadow-sm',
        cardClass: 'border-rose-500/35 bg-gradient-to-br from-rose-950/25 via-slate-900/90 to-slate-950/95 hover:border-rose-500/60 hover:from-rose-950/35 shadow-sm',
        textColor: 'text-rose-300'
      };
    }
    if (isExhausted) {
      return {
        badge: 'UNITS EXHAUSTED',
        badgeClass: 'bg-rose-500/20 text-rose-300 border-rose-500/40 shadow-sm',
        cardClass: 'border-rose-500/35 bg-gradient-to-br from-rose-950/25 via-slate-900/90 to-slate-950/95 hover:border-rose-500/60 hover:from-rose-950/35 shadow-sm',
        textColor: 'text-rose-300'
      };
    }
    if (days <= 3) {
      return {
        badge: days === 0 ? 'EXPIRES TODAY' : `${days}d REMAINING`,
        badgeClass: 'bg-rose-500/20 text-rose-300 border-rose-500/40 shadow-sm',
        cardClass: 'border-rose-500/35 bg-gradient-to-br from-rose-950/25 via-slate-900/90 to-slate-950/95 hover:border-rose-500/60 hover:from-rose-950/35 shadow-sm',
        textColor: 'text-rose-300'
      };
    }
    if (days <= 7) {
      return {
        badge: `${days}d REMAINING`,
        badgeClass: 'bg-amber-500/20 text-amber-300 border-amber-500/40 shadow-sm',
        cardClass: 'border-amber-500/35 bg-gradient-to-br from-amber-950/20 via-slate-900/90 to-slate-950/95 hover:border-amber-500/60 hover:from-amber-950/30 shadow-sm',
        textColor: 'text-amber-300'
      };
    }
    // 14–8 days: normal attention styling
    return {
      badge: `${days}d REMAINING`,
      badgeClass: 'bg-sky-500/10 text-sky-400 border-sky-500/30 shadow-sm',
      cardClass: 'border-slate-800/90 bg-gradient-to-br from-slate-900/90 via-sky-950/15 to-slate-950/95 hover:border-sky-500/40 hover:from-sky-950/25 shadow-sm',
      textColor: 'text-slate-300'
    };
  };

  // Helper for POC expiration styling:
  // 33–15 days: normal attention styling
  // 14–8 days: AMBER
  // 7–0 days: RED
  // expired: RED
  // missing POC: AMBER (NOT red)
  // conflict: PURPLE
  const getPocBadge = (poc: any) => {
    if (poc.isConflict || poc.poc_status === 'CONFLICT_NEEDS_REVIEW') {
      return {
        badge: 'POC CONFLICT',
        badgeClass: 'bg-purple-500/20 text-purple-300 border-purple-500/40 shadow-sm',
        cardClass: 'border-purple-500/35 bg-gradient-to-br from-purple-950/25 via-slate-900/90 to-slate-950/95 hover:border-purple-500/60 hover:from-purple-950/35 shadow-sm',
        subText: getPocExpirationDates(poc).length > 1
          ? `Conflicting dates (${getPocExpirationDates(poc).join(' vs ')}) — needs review`
          : 'Conflicting plan dates — needs review',
        textColor: 'text-purple-300'
      };
    }
    if (poc.isMissing) {
      return {
        badge: 'MISSING POC',
        badgeClass: isReferralCoordinator
          ? 'bg-[#080d19]/90 text-slate-200 border-slate-700/70 shadow-sm font-medium'
          : 'bg-[#180d19]/90 text-rose-200/95 border-rose-500/30 shadow-[0_1px_3px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.06)] font-medium',
        cardClass: isReferralCoordinator
          ? 'border-slate-800/90 bg-gradient-to-br from-slate-900/90 via-slate-900/60 to-slate-950/95 hover:border-slate-700 shadow-sm'
          : 'border-rose-500/30 bg-gradient-to-br from-rose-950/20 via-slate-900/90 to-slate-950/95 hover:border-rose-500/50 hover:from-rose-950/30 shadow-sm',
        subText: 'No POC on file — Initial / recert required',
        textColor: isReferralCoordinator ? 'text-slate-300' : 'text-rose-200/90'
      };
    }
    const days = typeof poc.daysRemaining === 'number' ? poc.daysRemaining : 999;
    if (days < 0) {
      return {
        badge: `EXPIRED (${Math.abs(days)}d AGO)`,
        badgeClass: 'bg-rose-500/20 text-rose-300 border-rose-500/40 shadow-sm',
        cardClass: 'border-rose-500/35 bg-gradient-to-br from-rose-950/25 via-slate-900/90 to-slate-950/95 hover:border-rose-500/60 hover:from-rose-950/35 shadow-sm',
        subText: 'Recertification overdue',
        textColor: 'text-rose-300'
      };
    }
    if (days <= 7) {
      return {
        badge: days === 0 ? 'EXPIRES TODAY' : `${days}d REMAINING`,
        badgeClass: 'bg-rose-500/20 text-rose-300 border-rose-500/40 shadow-sm',
        cardClass: 'border-rose-500/35 bg-gradient-to-br from-rose-950/25 via-slate-900/90 to-slate-950/95 hover:border-rose-500/60 hover:from-rose-950/35 shadow-sm',
        subText: `Recertification review urgent (${days}d)`,
        textColor: 'text-rose-300'
      };
    }
    if (days <= 14) {
      return {
        badge: `${days}d REMAINING`,
        badgeClass: isOwner
          ? 'bg-rose-950/40 text-rose-200/90 border-rose-500/30 shadow-sm font-medium'
          : 'bg-amber-500/20 text-amber-300 border-amber-500/40 shadow-sm',
        cardClass: isOwner
          ? 'border-rose-500/30 bg-gradient-to-br from-rose-950/20 via-slate-900/90 to-slate-950/95 hover:border-rose-500/50 hover:from-rose-950/30 shadow-sm'
          : 'border-amber-500/35 bg-gradient-to-br from-amber-950/20 via-slate-900/90 to-slate-950/95 hover:border-amber-500/60 hover:from-amber-950/30 shadow-sm',
        subText: `Approaching recertification (${days}d)`,
        textColor: isOwner ? 'text-rose-200/90' : 'text-amber-300'
      };
    }
    // 33–15 days: normal attention styling
    return {
      badge: `${days}d REMAINING`,
      badgeClass: 'bg-sky-500/10 text-sky-400 border-sky-500/30 shadow-sm',
      cardClass: 'border-slate-800/90 bg-gradient-to-br from-slate-900/90 via-sky-950/15 to-slate-950/95 hover:border-sky-500/40 hover:from-sky-950/25 shadow-sm',
      subText: `Recertification window open (${days}d)`,
      textColor: 'text-slate-300'
    };
  };

  const renderExpiringAuths = (isRc: boolean) => {
    const sortedAuths = [...activeExpiringAuths].sort((a, b) => {
      const aExhausted = a.isExhausted ? 1 : 0;
      const bExhausted = b.isExhausted ? 1 : 0;
      if (aExhausted !== bExhausted) return bExhausted - aExhausted;
      const aDays = typeof a.daysRemaining === 'number' ? a.daysRemaining : 999;
      const bDays = typeof b.daysRemaining === 'number' ? b.daysRemaining : 999;
      return aDays - bDays;
    });
    const displayedAuths = sortedAuths;
    const remainingAuthsCount = Math.max(0, activeExpiringAuths.length - displayedAuths.length);

    return (
      <div className={`relative overflow-hidden flex flex-col justify-between ${
        isOwner || isRc
          ? 'rounded-xl p-2.5 sm:p-3 bg-white border border-slate-200/90 shadow-xs transition-all h-full'
          : 'mobile-ops-shell rounded-lg p-2.5 sm:p-3 md:bg-gradient-to-br md:from-[#080d19] md:via-[#0b1220] md:to-[#070b14] border border-amber-500/35 hover:border-amber-400/50 shadow-[0_4px_20px_rgba(0,0,0,0.45),0_0_16px_rgba(245,158,11,0.07),inset_0_1px_0_rgba(255,255,255,0.06)] transition-all h-full group'
      }`}>
        {/* Subtle top edge hairline sheen for clinician view only */}
        {!isOwner && !isRc && (
          <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-amber-400/40 to-transparent pointer-events-none" />
        )}
        <div className={isRc ? 'space-y-1' : 'space-y-1.5'}>
          <div className={`flex justify-between items-center ${
            isOwner || isRc
              ? 'px-3 py-2 -mx-1 -mt-0.5 mb-2 rounded-lg bg-slate-50/90 border border-slate-200/90 relative overflow-hidden'
              : 'mobile-ops-header px-3 py-2 -mx-1 -mt-0.5 mb-2 rounded-lg md:bg-gradient-to-r md:from-[#1a2b44] md:via-[#142236] md:to-[#0e1726] border border-amber-500/40 md:border-amber-500/35 shadow-[0_2px_8px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.10)] relative overflow-hidden'
          }`}>
            <div className={`relative z-10 flex items-center ${isRc ? 'space-x-1.5' : 'space-x-2'}`}>
              <IconBadge icon={FileCheck2} variant="amber" size="xs" />
              <div className={`flex items-baseline ${isRc ? 'space-x-1.5' : 'space-x-2'}`}>
                <h3 className={`text-xs font-bold uppercase tracking-widest leading-none ${
                  isOwner || isRc ? 'text-slate-900' : 'text-slate-200'
                }`}>
                  Expiring Authorizations
                </h3>
                <span className={`${isRc ? 'text-[9px]' : 'text-[9.5px]'} hidden sm:inline leading-none ${
                  isOwner || isRc ? 'text-slate-500 font-medium' : 'text-slate-400'
                }`}>
                  All Clients <span className={isOwner || isRc ? 'text-slate-300' : 'text-slate-600'}>•</span>{' '}
                  <span className={`${isOwner || isRc ? 'text-slate-600 font-medium' : isOwner ? 'text-sky-300 font-medium' : 'text-amber-300 font-medium'} tracking-normal`}>14-Day Window & Low Units</span>
                </span>
              </div>
            </div>
            <div className={`relative z-10 flex items-center ${isRc ? 'space-x-1.5' : 'space-x-2'}`}>
              {activeExpiringAuths.length > 0 && (
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold font-mono leading-none ${
                  isOwner || isRc
                    ? 'bg-slate-100 text-slate-700 border border-slate-200'
                    : 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
                }`}>
                  {activeExpiringAuths.length}
                </span>
              )}
              <button
                onClick={() => onNavigateTab('clients', 'auth_only')}
                className={`font-semibold transition-colors flex items-center gap-0.5 cursor-pointer leading-none ${
                  isOwner || isRc
                    ? 'text-[10.5px] text-slate-600 hover:text-slate-900'
                    : 'text-[10.5px] text-sky-400 hover:text-sky-300'
                }`}
              >
                <span>View All</span>
                <ArrowRight className="w-2.5 h-2.5" />
              </button>
            </div>
          </div>

          {activeExpiringAuths.length === 0 ? (
            <div className={`${isRc ? 'py-5' : 'py-6'} text-center bg-white rounded-lg border border-slate-200 shadow-xs`}>
              <CheckCircle2 className="w-6 h-6 text-emerald-500 mx-auto mb-1.5 opacity-90" />
              <p className="text-xs text-slate-800 font-medium">
                No authorizations expiring within 14 days or exhausted.
              </p>
              <p className="text-[10px] text-slate-500 mt-0.5">All active treatments within safe unit margins.</p>
            </div>
          ) : isOwner || isRc ? (
            /* OWNER & RC VIEW: Structured Desktop/Tablet Table (CLIENT | URGENCY | EXPIRATION | REMINDER/ACTION) & Clean Mobile Stacked Rows */
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
              {/* DESKTOP/TABLET RESPONSIVE ROWS */}
              <div className="hidden md:block overflow-hidden">
                <div className="divide-y divide-slate-100">
                  {displayedAuths.map((auth) => {
                    const unitsUsed = Number(auth.units_used ?? 0);
                    const totalUnits = Number(auth.total_units ?? 0);
                    const availableUnits = auth.available_units !== undefined && auth.available_units !== null
                      ? Number(auth.available_units)
                      : Math.max(0, totalUnits - unitsUsed);
                    const days = typeof auth.daysRemaining === 'number' ? auth.daysRemaining : 999;

                    const isCriticallyLow = availableUnits < 6 || (totalUnits > 0 && unitsUsed >= totalUnits);

                    let dayDotClass = 'bg-slate-400';
                    let dayCountLabel = '—';
                    let dayTextColor = 'text-slate-600';
                    if (typeof auth.daysRemaining === 'number') {
                      if (days < 0) {
                        dayDotClass = 'bg-rose-500';
                        dayCountLabel = `${Math.abs(days)}D overdue`;
                        dayTextColor = 'text-rose-600 font-semibold';
                      } else if (days === 0) {
                        dayDotClass = 'bg-rose-500';
                        dayCountLabel = '0D overdue';
                        dayTextColor = 'text-rose-600 font-semibold';
                      } else if (days <= 7) {
                        dayDotClass = 'bg-amber-500';
                        dayCountLabel = `${days}D`;
                        dayTextColor = 'text-amber-700 font-semibold';
                      } else {
                        dayDotClass = 'bg-slate-400';
                        dayCountLabel = `${days}D`;
                        dayTextColor = 'text-slate-600';
                      }
                    }

                    const isEval = ['SEA', 'FEA'].includes(String(auth.auth_type || auth.focus_service_code || '').toUpperCase().trim());
                    const isUsedEval = isEval && (Number(auth.units_used || 0) > 0 || Number(auth.available_units || 0) <= 0);
                    const isActionable =
                      !['TERMINATED', 'CANCELED', 'VOIDED', 'REPLACED', 'INACTIVE'].includes(String(auth.raw_auth_status || auth.status || '').toUpperCase().trim()) &&
                      !isUsedEval &&
                      !auth.is_historical;

                    const is7DaySent = Boolean(auth.last_7day_notice);
                    const is3DaySent = Boolean(auth.last_3day_notice);

                    return (
                      <div
                        key={auth.id}
                        onClick={() => onSelectClient(auth.client_id)}
                        className="group relative overflow-hidden transition-colors duration-150 cursor-pointer select-none border-l-2 border-l-transparent px-3 py-2 bg-white odd:bg-white even:bg-slate-50/60 hover:bg-slate-100/80 hover:border-l-sky-500"
                        title={`Client: ${formatDisplayName(auth)} • Expiration: ${formatDateMMDDYY(auth.end_date)} • ${dayCountLabel}${totalUnits > 0 ? ` • Units: ${unitsUsed}/${totalUnits}` : ''}`}
                      >
                        <div className="relative z-10 flex items-center justify-between gap-x-2 sm:gap-x-3 w-full min-w-0">
                          {/* Client Name: prioritized, flex-1 truncate */}
                          <div className="font-semibold text-xs text-slate-900 group-hover:text-slate-950 transition-colors truncate min-w-0 flex-1">
                            {formatDisplayName(auth)}
                          </div>

                          {/* Right Cluster: Date • Urgency + Action Buttons locked inline */}
                          <div className="flex items-center gap-x-2 sm:gap-x-2.5 text-xs font-mono shrink-0 whitespace-nowrap">
                            {/* Expiration • Urgency */}
                            <div className="flex items-center gap-1.5 text-xs text-slate-700 shrink-0">
                              <span className="font-medium text-slate-800">{formatDateMMDDYY(auth.end_date)}</span>
                              <span className="text-slate-300">•</span>
                              <span className={`inline-flex items-center gap-1 text-[11px] ${dayTextColor}`}>
                                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dayDotClass}`} />
                                <span>{dayCountLabel}</span>
                              </span>
                              {isCriticallyLow && (
                                <span className="hidden 2xl:inline text-[10px] text-rose-600 font-sans ml-0.5 font-semibold">
                                  ({unitsUsed}/{totalUnits})
                                </span>
                              )}
                            </div>

                            {/* Reminder/Action buttons */}
                            <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                              {isActionable ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => is7DaySent ? setSelectedAuthHistory({ auth, noticeType: '7_DAY' }) : onOpenAuthEmail?.(auth.client_id, 'auth_7day', auth)}
                                    className={`px-2 py-0.5 text-[9px] font-semibold rounded-md border flex items-center gap-1 transition-all cursor-pointer shrink-0 leading-none ${
                                      is7DaySent
                                        ? 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200/70 shadow-none'
                                        : 'bg-sky-50 text-sky-700 hover:bg-sky-100 hover:text-sky-800 border-sky-200 hover:border-sky-300 shadow-xs'
                                    }`}
                                    title={is7DaySent ? `7-Day Notice Sent ${formatNoticeTimestamp(auth.last_7day_notice?.sent_at)}` : "Draft 7-Day Expiration Notice Email"}
                                  >
                                    <Mail className={`w-2.5 h-2.5 ${is7DaySent ? 'text-slate-400' : 'text-sky-600'}`} />
                                    <span className="tracking-wide">{is7DaySent ? '7d Sent' : '7-Day'}</span>
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => is3DaySent ? setSelectedAuthHistory({ auth, noticeType: '3_DAY' }) : onOpenAuthEmail?.(auth.client_id, 'auth_3day', auth)}
                                    className={`px-2 py-0.5 text-[9px] font-semibold rounded-md border flex items-center gap-1 transition-all cursor-pointer shrink-0 leading-none ${
                                      is3DaySent
                                        ? 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200/70 shadow-none'
                                        : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 hover:text-indigo-800 border-indigo-200 hover:border-indigo-300 shadow-xs'
                                    }`}
                                    title={is3DaySent ? `3-Day Notice Sent ${formatNoticeTimestamp(auth.last_3day_notice?.sent_at)}` : "Draft Urgent 3-Day Expiration Notice Email"}
                                  >
                                    <Mail className={`w-2.5 h-2.5 ${is3DaySent ? 'text-slate-400' : 'text-indigo-600'}`} />
                                    <span className="tracking-wide">{is3DaySent ? '3d Sent' : '3-Day'}</span>
                                  </button>
                                </>
                              ) : (
                                <span className="text-[9.5px] text-slate-400 italic">No notice needed</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* MOBILE VIEW: Clean Stacked Rows (Client Name / Units · Days / 7-Day | 3-Day) */}
              <div className="block md:hidden divide-y divide-slate-100">
                {displayedAuths.map((auth, idx) => {
                  const unitsUsed = Number(auth.units_used ?? 0);
                  const totalUnits = Number(auth.total_units ?? 0);
                  const availableUnits = auth.available_units !== undefined && auth.available_units !== null
                    ? Number(auth.available_units)
                    : Math.max(0, totalUnits - unitsUsed);
                  const days = typeof auth.daysRemaining === 'number' ? auth.daysRemaining : 999;

                  const isCriticallyLow = availableUnits < 6 || (totalUnits > 0 && unitsUsed >= totalUnits);
                  const unitsUsedColor = isCriticallyLow ? 'text-rose-600 font-semibold' : 'text-slate-600';

                  let dayDotClass = 'bg-slate-400';
                  let dayCountLabel = '—';
                  if (typeof auth.daysRemaining === 'number') {
                    if (days < 0) {
                      dayDotClass = 'bg-rose-500';
                      dayCountLabel = `${Math.abs(days)}D overdue`;
                    } else if (days === 0) {
                      dayDotClass = 'bg-rose-500';
                      dayCountLabel = '0D overdue';
                    } else if (days <= 7) {
                      dayDotClass = 'bg-amber-500';
                      dayCountLabel = `${days}D`;
                    } else {
                      dayDotClass = 'bg-slate-400';
                      dayCountLabel = `${days}D`;
                    }
                  }

                  const isEval = ['SEA', 'FEA'].includes(String(auth.auth_type || auth.focus_service_code || '').toUpperCase().trim());
                  const isUsedEval = isEval && (Number(auth.units_used || 0) > 0 || Number(auth.available_units || 0) <= 0);
                  const isActionable =
                    !['TERMINATED', 'CANCELED', 'VOIDED', 'REPLACED', 'INACTIVE'].includes(String(auth.raw_auth_status || auth.status || '').toUpperCase().trim()) &&
                    !isUsedEval &&
                    !auth.is_historical;

                  const is7DaySent = Boolean(auth.last_7day_notice);
                  const is3DaySent = Boolean(auth.last_3day_notice);

                  return (
                    <div
                      key={`mobile-auth-${auth.id}`}
                      onClick={() => onSelectClient(auth.client_id)}
                      className={`p-3 transition-colors cursor-pointer select-none ${
                        idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/70'
                      } hover:bg-slate-100/80 active:bg-slate-200/80`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        {/* Client Name & Units · Days */}
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-bold text-slate-900 truncate">
                            {formatDisplayName(auth)}
                          </div>
                          <div className="text-[11px] font-mono text-slate-500 mt-1 flex items-center gap-1.5 flex-wrap">
                            <span className={unitsUsedColor}>
                              {unitsUsed}/{totalUnits} units
                            </span>
                            <span className="text-slate-300">·</span>
                            <span className="inline-flex items-center gap-1 text-slate-700">
                              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dayDotClass}`} />
                              <span>{dayCountLabel}</span>
                            </span>
                          </div>
                        </div>

                        {/* 7-Day | 3-Day Action Buttons */}
                        <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                          {isActionable ? (
                            <>
                              <button
                                type="button"
                                onClick={() => is7DaySent ? setSelectedAuthHistory({ auth, noticeType: '7_DAY' }) : onOpenAuthEmail?.(auth.client_id, 'auth_7day', auth)}
                                className={`px-2.5 py-1 text-[10px] font-semibold rounded-md border flex items-center gap-1 transition-all cursor-pointer leading-none ${
                                  is7DaySent
                                    ? 'bg-slate-100 text-slate-500 border-slate-200'
                                    : 'bg-sky-50 text-sky-700 border-sky-200 active:scale-95'
                                }`}
                              >
                                <Mail className={`w-3 h-3 ${is7DaySent ? 'text-slate-400' : 'text-sky-600'}`} />
                                <span>{is7DaySent ? '7d' : '7-Day'}</span>
                              </button>

                              <button
                                type="button"
                                onClick={() => is3DaySent ? setSelectedAuthHistory({ auth, noticeType: '3_DAY' }) : onOpenAuthEmail?.(auth.client_id, 'auth_3day', auth)}
                                className={`px-2.5 py-1 text-[10px] font-semibold rounded-md border flex items-center gap-1 transition-all cursor-pointer leading-none ${
                                  is3DaySent
                                    ? 'bg-slate-100 text-slate-500 border-slate-200'
                                    : 'bg-indigo-50 text-indigo-700 border-indigo-200 active:scale-95'
                                }`}
                              >
                                <Mail className={`w-3 h-3 ${is3DaySent ? 'text-slate-400' : 'text-indigo-600'}`} />
                                <span>{is3DaySent ? '3d' : '3-Day'}</span>
                              </button>
                            </>
                          ) : (
                            <span className="text-[10px] text-slate-400 italic">No notice needed</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className={`overflow-hidden border border-slate-800/80 divide-y divide-slate-800/50 ${isRc ? 'rounded-lg bg-[#070b13]/90' : 'rounded bg-[#0c1322]'}`}>
              {displayedAuths.map((auth, idx) => {
                const unitsUsed = Number(auth.units_used ?? 0);
                const totalUnits = Number(auth.total_units ?? 0);
                const availableUnits = auth.available_units !== undefined && auth.available_units !== null
                  ? Number(auth.available_units)
                  : Math.max(0, totalUnits - unitsUsed);
                const days = typeof auth.daysRemaining === 'number' ? auth.daysRemaining : 999;

                // Units formatting: clinical normal text, only restrained soft red when critically low / exhausted
                const isCriticallyLow = availableUnits < 6 || (totalUnits > 0 && unitsUsed >= totalUnits);
                const unitsUsedColor = isCriticallyLow ? 'text-rose-300/90 font-medium' : 'text-slate-300';

                // Clean day-count dot + text
                let dayDotClass = 'bg-slate-400 shadow-[0_0_3px_rgba(148,163,184,0.4)]';
                let dayCountLabel = '—';
                if (typeof auth.daysRemaining === 'number') {
                  if (days < 0) {
                    dayDotClass = 'bg-rose-500 shadow-[0_0_4px_rgba(244,63,94,0.7)]';
                    dayCountLabel = `${Math.abs(days)}D overdue`;
                  } else if (days === 0) {
                    dayDotClass = 'bg-rose-500 shadow-[0_0_4px_rgba(244,63,94,0.7)]';
                    dayCountLabel = '0D overdue';
                  } else if (days <= 7) {
                    dayDotClass = isOwner ? 'bg-cyan-400 shadow-[0_0_4px_rgba(6,182,212,0.7)]' : 'bg-amber-400 shadow-[0_0_4px_rgba(245,158,11,0.7)]';
                    dayCountLabel = `${days}D`;
                  } else {
                    dayDotClass = 'bg-slate-400 shadow-[0_0_3px_rgba(148,163,184,0.4)]';
                    dayCountLabel = `${days}D`;
                  }
                }

                const isEval = ['SEA', 'FEA'].includes(String(auth.auth_type || auth.focus_service_code || '').toUpperCase().trim());
                const isUsedEval = isEval && (Number(auth.units_used || 0) > 0 || Number(auth.available_units || 0) <= 0);
                const isActionable =
                  !['TERMINATED', 'CANCELED', 'VOIDED', 'REPLACED', 'INACTIVE'].includes(String(auth.raw_auth_status || auth.status || '').toUpperCase().trim()) &&
                  !isUsedEval &&
                  !auth.is_historical;

                const is7DaySent = Boolean(auth.last_7day_notice);
                const is3DaySent = Boolean(auth.last_3day_notice);

                return (
                  <div
                    key={auth.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelectClient(auth.client_id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelectClient(auth.client_id);
                      }
                    }}
                    className={`group relative overflow-hidden px-3.5 py-1.5 sm:py-2 ${
                      idx % 2 === 0 ? 'mobile-ops-row-a' : 'mobile-ops-row-b'
                    } transition-all duration-150 ease-out focus:outline-none focus-visible:ring-1 ${
                      isRc ? 'focus-visible:ring-pink-400/60' : 'focus-visible:ring-cyan-400/60'
                    } flex flex-col justify-center gap-1 cursor-pointer select-none`}
                    title="Click to view client chart"
                  >
                    {/* Restrained glass/chrome sheen across the row on hover */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.03] to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out pointer-events-none" />

                    {/* Line 1: Client name left, 7-Day & 3-Day actions right */}
                    <div className="relative z-10 flex items-center justify-between gap-2 min-w-0">
                      <span
                        className={`font-semibold text-slate-100 ${
                          isRc ? 'group-hover:text-pink-200' : 'group-hover:text-cyan-300'
                        } transition-colors text-xs truncate`}
                        title={formatDisplayName(auth)}
                      >
                        {formatDisplayName(auth)}
                      </span>

                      {isActionable ? (
                        <div
                          className="flex items-center gap-1.5 shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* 7-Day notice action */}
                          <button
                            type="button"
                            onClick={() => onOpenAuthEmail?.(auth.client_id, 'auth_7day', auth)}
                            className={`px-2 py-0.5 text-[9px] font-semibold rounded-md border flex items-center gap-1 transition-all cursor-pointer shrink-0 leading-none ${
                              is7DaySent
                                ? 'bg-slate-800/80 text-slate-400 border-slate-700/60 hover:text-slate-300 shadow-none'
                                : isRc
                                ? 'bg-gradient-to-b from-[#141525] via-[#0d101d] to-[#070913] hover:from-[#1a1c30] hover:to-[#0a0d1a] text-pink-200/95 hover:text-pink-100 border-pink-400/40 hover:border-pink-300/70 shadow-[0_1px_3px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.16)] hover:shadow-[0_2px_6px_rgba(0,0,0,0.6),0_0_8px_rgba(244,114,182,0.18),inset_0_1px_0_rgba(255,255,255,0.22)] active:scale-98'
                                : 'bg-gradient-to-b from-[#092226] via-[#06191c] to-[#041114] hover:from-[#0c2f34] hover:to-[#071f24] text-emerald-300 hover:text-emerald-100 border-emerald-500/40 hover:border-emerald-400/70 shadow-[0_1px_4px_rgba(0,0,0,0.4),0_0_8px_rgba(16,185,129,0.18),inset_0_1px_0_rgba(255,255,255,0.15)] hover:shadow-[0_0_12px_rgba(52,211,153,0.3),inset_0_1px_0_rgba(255,255,255,0.22)] active:scale-98'
                            }`}
                            title="Draft 7-Day Expiration Notice Email to Support Coordinator"
                          >
                            <Mail className={`w-2.5 h-2.5 ${is7DaySent ? 'text-slate-500' : isRc ? 'text-pink-300' : 'text-emerald-400'}`} />
                            <span className="tracking-wide">7-Day</span>
                          </button>

                          {/* 3-Day notice action */}
                          <button
                            type="button"
                            onClick={() => onOpenAuthEmail?.(auth.client_id, 'auth_3day', auth)}
                            className={`px-2 py-0.5 text-[9px] font-semibold rounded-md border flex items-center gap-1 transition-all cursor-pointer shrink-0 leading-none ${
                              is3DaySent
                                ? 'bg-slate-800/80 text-slate-400 border-slate-700/60 hover:text-slate-300 shadow-none'
                                : isRc
                                ? 'bg-gradient-to-b from-[#131427] via-[#0d0e1f] to-[#060714] hover:from-[#191b33] hover:to-[#090b1c] text-purple-200/95 hover:text-purple-100 border-purple-400/40 hover:border-purple-300/70 shadow-[0_1px_3px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.16)] hover:shadow-[0_2px_6px_rgba(0,0,0,0.6),0_0_8px_rgba(168,85,247,0.18),inset_0_1px_0_rgba(255,255,255,0.22)] active:scale-98'
                                : 'bg-gradient-to-b from-[#18112c] via-[#110d22] to-[#0a0817] hover:from-[#22183c] hover:to-[#100c24] text-violet-200/95 hover:text-violet-100 border-violet-500/40 hover:border-violet-400/70 shadow-[0_1px_4px_rgba(0,0,0,0.4),0_0_8px_rgba(139,92,246,0.18),inset_0_1px_0_rgba(255,255,255,0.16)] hover:shadow-[0_0_12px_rgba(167,139,250,0.3),inset_0_1px_0_rgba(255,255,255,0.24)] active:scale-98'
                            }`}
                            title="Draft Urgent 3-Day Expiration Notice Email to Support Coordinator"
                          >
                            <Mail className={`w-2.5 h-2.5 ${is3DaySent ? 'text-slate-500' : isRc ? 'text-purple-300' : 'text-violet-300'}`} />
                            <span className="tracking-wide">3-Day</span>
                          </button>
                        </div>
                      ) : (
                        <span className="text-[10px] text-slate-500 italic shrink-0">No notice needed</span>
                      )}
                    </div>

                    {/* Line 2: Units, Expiration date, compact day status, and subdued Not sent/Sent audit status */}
                    <div className="relative z-10 flex items-center justify-between gap-2 min-w-0 text-[10px] font-mono text-slate-400">
                      <div className="flex items-center gap-2.5 flex-wrap min-w-0">
                        <span>
                          Units: <span className={unitsUsedColor}>{unitsUsed}</span><span className="text-slate-600">/</span><span className="text-slate-400">{totalUnits}</span>
                        </span>

                        <span>
                          Exp: <span className="text-slate-300">{formatDateMMDDYY(auth.end_date)}</span>
                        </span>

                        <span className="inline-flex items-center gap-1.5 font-mono text-[9.5px]">
                          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dayDotClass}`} />
                          <span className="text-slate-200 font-medium">{dayCountLabel}</span>
                        </span>
                      </div>

                      {isActionable && (
                        <div
                          className="flex items-center gap-1.5 shrink-0 text-[8.5px] text-slate-500"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span>
                            7d:{' '}
                            {auth.last_7day_notice ? (
                              <button
                                type="button"
                                onClick={() => setSelectedAuthHistory({ auth, noticeType: '7_DAY' })}
                                className={`text-slate-300 ${
                                  isRc ? 'hover:text-pink-300' : 'hover:text-sky-300'
                                } font-medium hover:underline transition-colors cursor-pointer`}
                                title={`Sender: ${auth.last_7day_notice.sender_name || auth.last_7day_notice.sender_email || 'Staff'}\nTo: ${auth.last_7day_notice.recipient_emails}\nClick for audit log`}
                              >
                                Sent {formatNoticeTimestamp(auth.last_7day_notice.sent_at)}
                              </button>
                            ) : (
                              <span className="text-slate-500">Not sent</span>
                            )}
                          </span>
                          <span className="text-slate-700">·</span>
                          <span>
                            3d:{' '}
                            {auth.last_3day_notice ? (
                              <button
                                type="button"
                                onClick={() => setSelectedAuthHistory({ auth, noticeType: '3_DAY' })}
                                className={`text-slate-300 ${
                                  isRc ? 'hover:text-purple-300' : 'hover:text-violet-300'
                                } font-medium hover:underline transition-colors cursor-pointer`}
                                title={`Sender: ${auth.last_3day_notice.sender_name || auth.last_3day_notice.sender_email || 'Staff'}\nTo: ${auth.last_3day_notice.recipient_emails}\nClick for audit log`}
                              >
                                Sent {formatNoticeTimestamp(auth.last_3day_notice.sent_at)}
                              </button>
                            ) : (
                              <span className="text-slate-500">Not sent</span>
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {remainingAuthsCount > 0 && (
          <button
            onClick={() => onNavigateTab('clients', 'auth_only')}
            className={`w-full text-center font-medium transition-colors cursor-pointer ${
              isOwner || isRc
                ? 'text-[10.5px] py-1.5 mt-2 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 hover:text-slate-900 shadow-2xs flex items-center justify-center gap-1'
                : 'text-[11px] text-sky-400 hover:text-sky-300 py-1 mt-1.5 rounded bg-[#0e1420] md:bg-[#090e1a] border border-slate-700/80 md:border-slate-800/80 hover:border-slate-700'
            }`}
          >
            {isOwner || isRc ? (
              <>
                <span>View All ({remainingAuthsCount} more)</span>
                <ArrowRight className="w-2.5 h-2.5" />
              </>
            ) : (
              `+ ${remainingAuthsCount} more active authorizations requiring review →`
            )}
          </button>
        )}
      </div>
    );
  };

  const renderExpiringPocs = (isRc: boolean) => {
    const displayLimit = isOwner ? 5 : isRc ? 13 : 15;
    const targetPocs = isRc ? rcExpiringPocs : ownerExpiringPocs;
    const sortedPocs = [...targetPocs].sort((a, b) => {
      const aUrgent = (a.isMissing || a.isConflict || a.poc_status === 'CONFLICT_NEEDS_REVIEW') ? 1 : 0;
      const bUrgent = (b.isMissing || b.isConflict || b.poc_status === 'CONFLICT_NEEDS_REVIEW') ? 1 : 0;
      if (aUrgent !== bUrgent) return bUrgent - aUrgent;
      const aDays = typeof a.daysRemaining === 'number' ? a.daysRemaining : 999;
      const bDays = typeof b.daysRemaining === 'number' ? b.daysRemaining : 999;
      return aDays - bDays;
    });
    const displayedPocs = sortedPocs.slice(0, displayLimit);
    const remainingPocsCount = Math.max(0, targetPocs.length - displayedPocs.length);

    return (
      <div className={`relative overflow-hidden flex flex-col justify-between ${
        isOwner || isRc
          ? 'rounded-xl p-2.5 sm:p-3 bg-white border border-slate-200/90 shadow-xs transition-all h-full'
          : 'mobile-ops-shell rounded-lg p-2.5 sm:p-3 md:bg-gradient-to-br md:from-[#080d19] md:via-[#0f1024] md:to-[#070b14] border border-slate-700/80 hover:border-slate-600 shadow-[0_4px_20px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)] transition-all h-full group'
      }`}>
        <div className={isRc ? 'space-y-1' : 'space-y-1.5'}>
          <div className={`flex justify-between items-center ${
            isOwner || isRc
              ? 'px-3 py-2 -mx-1 -mt-0.5 mb-2 rounded-lg bg-slate-50/90 border border-slate-200/90 relative overflow-hidden'
              : 'mobile-ops-header px-3 py-2 -mx-1 -mt-0.5 mb-2 rounded-lg md:bg-gradient-to-r md:from-[#1a2b44] md:via-[#142236] md:to-[#0e1726] border border-slate-700/80 shadow-[0_2px_8px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.10)] relative overflow-hidden'
          }`}>
            <div className={`relative z-10 flex items-center ${isRc ? 'space-x-1.5' : 'space-x-2'}`}>
              <IconBadge icon={Stethoscope} variant="blue" size="xs" />
              <div className={`flex items-baseline ${isRc ? 'space-x-1.5' : 'space-x-2'}`}>
                <h3 className={`text-xs font-bold uppercase tracking-widest leading-none ${
                  isOwner || isRc ? 'text-slate-900' : 'text-slate-200'
                }`}>
                  Expiring Plans of Care (POC)
                </h3>
                <span className={`${isRc ? 'text-[9px]' : 'text-[9.5px]'} hidden sm:inline leading-none ${
                  isOwner || isRc ? 'text-slate-500 font-medium' : 'text-slate-400'
                }`}>
                  All Clients <span className={isOwner || isRc ? 'text-slate-300' : 'text-slate-600'}>•</span>{' '}
                  <span className={`${isOwner || isRc ? 'text-slate-600 font-medium' : 'text-slate-300 font-medium'} tracking-normal`}>
                    {isRc ? '30-Day Window' : '14-Day Window'}
                  </span>
                </span>
              </div>
            </div>
            <div className={`relative z-10 flex items-center ${isRc ? 'space-x-1.5' : 'space-x-2'}`}>
              {targetPocs.length > 0 && (
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold font-mono leading-none ${
                  isOwner || isRc
                    ? 'bg-slate-100 text-slate-700 border border-slate-200'
                    : 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
                }`}>
                  {targetPocs.length}
                </span>
              )}
              <button
                onClick={() => onNavigateTab('clients', 'poc_only')}
                className={`font-semibold transition-colors flex items-center gap-0.5 cursor-pointer leading-none ${
                  isOwner || isRc
                    ? 'text-[10.5px] text-slate-600 hover:text-slate-900'
                    : 'text-[10.5px] text-sky-400 hover:text-sky-300'
                }`}
              >
                <span>{isOwner || isRc ? 'View All' : 'Expirations'}</span>
                <ArrowRight className="w-2.5 h-2.5" />
              </button>
            </div>
          </div>

          {targetPocs.length === 0 ? (
            <div className={`${isRc ? 'py-5' : 'py-6'} text-center bg-white rounded-lg border border-slate-200 shadow-xs`}>
              <CheckCircle2 className="w-6 h-6 text-emerald-500 mx-auto mb-1.5 opacity-90" />
              <p className="text-xs text-slate-800 font-medium">
                No plans of care currently approaching expiration or missing.
              </p>
              <p className="text-[10px] text-slate-500 mt-0.5">
                All agency compliance cycles up to date.
              </p>
            </div>
          ) : isOwner || isRc ? (
            /* OWNER & RC VIEW: Structured Desktop/Tablet Table (CLIENT | STATUS | EXPIRATION) & Clean Mobile Stacked Rows */
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
              {/* DESKTOP/TABLET RESPONSIVE ROWS */}
              <div className="hidden md:block overflow-hidden">
                <div className="divide-y divide-slate-100">
                  {displayedPocs.map((poc: any) => {
                    const isConflict = poc.isConflict || poc.poc_status === 'CONFLICT_NEEDS_REVIEW';
                    const isMissing = poc.isMissing;
                    const days = typeof poc.daysRemaining === 'number' ? poc.daysRemaining : 999;
                    const pocDates = getPocExpirationDates(poc);

                    let daysStatusText = '—';
                    if (!isMissing && !isConflict) {
                      daysStatusText = `${days}D`;
                    }

                    let pocStatusLabel = 'Active Cycle';
                    let stateChipText = `${daysStatusText}`;
                    let statusDot = <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-slate-400" />;

                    if (isConflict) {
                      pocStatusLabel = 'Conflict';
                      stateChipText = 'POC Conflict';
                      statusDot = <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-amber-500" />;
                    } else if (isMissing) {
                      pocStatusLabel = 'No POC on file';
                      stateChipText = 'Missing';
                      statusDot = <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-rose-500" />;
                    } else if (days < 0 || days === 0) {
                      pocStatusLabel = 'Past Expiration Date';
                      stateChipText = 'Expired';
                      statusDot = <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-rose-500" />;
                    } else {
                      pocStatusLabel = 'Recertification Required';
                      if (days <= 3) {
                        statusDot = <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-rose-500" />;
                      } else if (days <= 7) {
                        statusDot = <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-amber-500" />;
                      }
                    }

                    return (
                      <div
                        key={poc.listKey || poc.id}
                        onClick={() => onSelectClient(poc.id)}
                        className="group relative overflow-hidden transition-colors duration-150 cursor-pointer select-none border-l-2 border-l-transparent px-3 py-2 bg-white odd:bg-white even:bg-slate-50/60 hover:bg-slate-100/80 hover:border-l-sky-500"
                        title="Click to view client chart"
                      >
                        <div className="relative z-10 flex items-center justify-between gap-x-2 sm:gap-x-3 w-full min-w-0">
                          {/* Client Name: prioritized, flex-1 truncate */}
                          <div className="font-semibold text-xs text-slate-900 group-hover:text-slate-950 transition-colors truncate min-w-0 flex-1">
                            {formatPocReminderName(poc)}
                          </div>

                          {/* Right Cluster: Status + Expiration dates locked inline */}
                          <div className="flex items-center gap-x-2 sm:gap-x-2.5 text-xs shrink-0 whitespace-nowrap">
                            {/* Status */}
                            <div className="flex items-center gap-1.5 text-xs text-slate-700 shrink-0 font-mono">
                              {statusDot}
                              <span className="font-semibold text-slate-700 text-[11px]">{stateChipText}</span>
                              {isConflict && pocDates.length > 1 && (
                                <span className="hidden xl:inline text-slate-500 text-[10px]">
                                  ({pocDates.join(' vs ')})
                                </span>
                              )}
                            </div>

                            <span className="text-slate-300 font-mono">•</span>

                            {/* Expiration */}
                            <div className="text-xs font-mono text-slate-600 shrink-0">
                              {isMissing || pocDates.length === 0 ? (
                                <span className="text-slate-400 text-[11px]">No date</span>
                              ) : pocDates.length > 1 ? (
                                <div className="flex items-center gap-1 text-[11px] text-slate-700 font-medium">
                                  {pocDates.join(' • ')}
                                </div>
                              ) : (
                                <span className="text-slate-700 font-medium text-[11px]">
                                  {pocDates[0]}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* MOBILE VIEW: Clean Stacked Rows (Client Name / Expiration · State) */}
              <div className="block md:hidden divide-y divide-slate-100">
                {displayedPocs.map((poc: any, idx: number) => {
                  const isConflict = poc.isConflict || poc.poc_status === 'CONFLICT_NEEDS_REVIEW';
                  const isMissing = poc.isMissing;
                  const days = typeof poc.daysRemaining === 'number' ? poc.daysRemaining : 999;
                  const pocDates = getPocExpirationDates(poc);

                  let daysStatusText = '—';
                  if (!isMissing && !isConflict) {
                    daysStatusText = `${days}D`;
                  }

                  let stateChipText = `${daysStatusText}`;
                  let statusDotMobile = <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-slate-400" />;

                  if (isConflict) {
                    stateChipText = 'Conflict';
                    statusDotMobile = <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-amber-500" />;
                  } else if (isMissing) {
                    stateChipText = 'Missing';
                    statusDotMobile = <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-rose-500" />;
                  } else if (days < 0 || days === 0) {
                    stateChipText = 'Expired';
                    statusDotMobile = <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-rose-500" />;
                  } else if (days <= 3) {
                    statusDotMobile = <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-rose-500" />;
                  } else if (days <= 7) {
                    statusDotMobile = <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-amber-500" />;
                  }

                  const expirationText = isMissing
                    ? 'No POC on file'
                    : pocDates[0] || formatDateMMDDYY(poc.poc_end_date) || '—';

                  return (
                    <div
                      key={`mobile-poc-${poc.listKey || poc.id}`}
                      onClick={() => onSelectClient(poc.id)}
                      className={`p-3 transition-colors cursor-pointer select-none ${
                        idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/70'
                      } hover:bg-slate-100/80 active:bg-slate-200/80`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-bold text-slate-900 truncate">
                            {formatPocReminderName(poc)}
                          </div>
                          <div className="text-[11px] font-mono text-slate-500 mt-1 flex items-center gap-1.5 flex-wrap">
                            <span className="text-slate-700 font-medium">
                              {expirationText}
                            </span>
                            <span className="text-slate-300">·</span>
                            <span className="inline-flex items-center gap-1.5 text-slate-700 font-medium">
                              {statusDotMobile}
                              <span>{stateChipText}</span>
                            </span>
                          </div>
                        </div>

                        <div className="shrink-0">
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[9.5px] font-mono leading-none bg-slate-100 border border-slate-200 text-slate-700 font-medium">
                            {statusDotMobile}
                            <span>{stateChipText}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className={`overflow-hidden border border-slate-800/80 divide-y divide-slate-800/50 ${isRc ? 'rounded-lg bg-[#070b13]/90' : 'rounded bg-[#0c1322]'}`}>
              {displayedPocs.map((poc: any, idx: number) => {
                const isConflict = poc.isConflict || poc.poc_status === 'CONFLICT_NEEDS_REVIEW';
                const isMissing = poc.isMissing;
                const days = typeof poc.daysRemaining === 'number' ? poc.daysRemaining : 999;
                const fallbackPocDates = getPocExpirationDates(poc);

                // Flat day badge
                let daysStatusText = '—';
                let daysTagClass = 'bg-slate-800/80 text-slate-300 border-slate-700/60 font-medium';
                if (!isMissing && !isConflict) {
                  if (days < 0) {
                    daysStatusText = `${days}D`;
                    daysTagClass = 'bg-rose-500/20 text-rose-300 border-rose-500/40 font-semibold';
                  } else if (days === 0) {
                    daysStatusText = '0D';
                    daysTagClass = 'bg-rose-500/20 text-rose-300 border-rose-500/40 font-semibold';
                  } else if (days <= 3) {
                    daysStatusText = `${days}D`;
                    daysTagClass = 'bg-rose-500/15 text-rose-200 border-rose-400/35 font-semibold';
                  } else if (days <= 7) {
                    daysStatusText = `${days}D`;
                    daysTagClass = isOwner
                      ? 'bg-rose-950/40 text-rose-200/90 border-rose-500/30 font-medium'
                      : 'bg-amber-500/15 text-amber-300 border-amber-500/30 font-medium';
                  } else {
                    daysStatusText = `${days}D`;
                    daysTagClass = 'bg-slate-800/80 text-slate-300 border-slate-700/60 font-medium';
                  }
                }

                // Clean status chip: restrained rose/plum for Missing POC, purple for Conflict, rose for Expired
                let pocStatusText = '';
                let pocStatusClass = 'bg-slate-800/80 text-slate-300 border-slate-700/60';
                if (isConflict) {
                  pocStatusText = 'POC Conflict';
                  pocStatusClass = 'bg-amber-500/15 text-amber-300 border-amber-500/30 font-semibold';
                } else if (isMissing) {
                  pocStatusText = 'Missing POC';
                  // Clean restrained navy chip with rose indicator for RC, plum/rose for Owner
                  pocStatusClass = isRc
                    ? 'bg-[#080d19]/90 text-slate-200 border-slate-700/70 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] font-medium'
                    : 'bg-[#180d19]/90 text-rose-200/95 border-rose-500/30 shadow-[0_1px_3px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.06)] font-medium';
                } else if (days < 0 || days === 0) {
                  pocStatusText = 'Expired';
                  pocStatusClass = 'bg-rose-500/20 text-rose-300 border-rose-500/40 font-semibold';
                } else {
                  pocStatusText = 'Expiring';
                  pocStatusClass = isOwner
                    ? 'bg-rose-950/40 text-rose-200/90 border-rose-500/30 font-medium'
                    : 'bg-amber-500/15 text-amber-200 border-amber-500/30';
                }

                return (
                  <div
                    key={poc.listKey || poc.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelectClient(poc.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelectClient(poc.id);
                      }
                    }}
                    className={`group relative overflow-hidden px-3.5 py-1.5 sm:py-2 ${
                      idx % 2 === 0 ? 'mobile-ops-row-a' : 'mobile-ops-row-b'
                    } transition-all duration-150 ease-out focus:outline-none focus-visible:ring-1 ${
                      isRc ? 'focus-visible:ring-pink-400/60' : 'focus-visible:ring-cyan-400/60'
                    } flex flex-col justify-center gap-1 cursor-pointer select-none`}
                    title="Click to view client chart"
                  >
                    {/* Restrained glass/chrome sheen across the row on hover */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.03] to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out pointer-events-none" />

                    {/* Line 1: Client name left, status chip right */}
                    <div className="relative z-10 flex items-center justify-between gap-2 min-w-0">
                      <span
                        className={`font-semibold text-slate-100 ${
                          isRc ? 'group-hover:text-pink-200' : 'group-hover:text-cyan-300'
                        } transition-colors text-xs truncate`}
                        title={formatPocReminderName(poc)}
                      >
                        {formatPocReminderName(poc)}
                      </span>

                      <div className="shrink-0 flex items-center">
                        <span className={`px-2 py-0.5 rounded text-[8.5px] font-mono font-semibold uppercase tracking-wider whitespace-nowrap leading-none border inline-flex items-center ${pocStatusClass}`}>
                          {isRc && isMissing && (
                            <span className="w-1.5 h-1.5 rounded-full bg-rose-400 shadow-[0_0_4px_rgba(244,63,94,0.6)] mr-1.5 shrink-0" />
                          )}
                          {pocStatusText}
                        </span>
                      </div>
                    </div>

                    {/* Line 2: Supporting POC status / expiration */}
                    <div className="relative z-10 flex items-center justify-between gap-2 min-w-0 text-[10px] font-mono text-slate-400">
                      <div className="flex items-center gap-2.5 flex-wrap min-w-0">
                        {isMissing ? (
                          <span className={`font-sans text-[10.5px] flex items-center gap-1.5 ${isRc ? 'text-slate-300' : isOwner ? 'text-rose-200/90' : 'text-amber-300/80'}`}>
                            {isRc && (
                              <span className="w-1.5 h-1.5 rounded-full bg-rose-400 shadow-[0_0_4px_rgba(244,63,94,0.6)] shrink-0" />
                            )}
                            <span>No POC on file — Evaluation / Recert required</span>
                          </span>
                        ) : isConflict ? (
                          <span className="text-purple-300/80 font-sans text-[10.5px]">
                            {fallbackPocDates.length > 1 ? `Conflict (${fallbackPocDates.join(' vs ')})` : 'Conflict'}
                          </span>
                        ) : (
                          <>
                            <span className="inline-flex items-center gap-1.5 flex-wrap">
                              Exp:{' '}
                              {fallbackPocDates.length > 1 ? (
                                <span className="inline-flex flex-col gap-0.5 leading-tight text-slate-300">
                                  {fallbackPocDates.map((d, idx) => (
                                    <span key={idx}>{d}</span>
                                  ))}
                                </span>
                              ) : (
                                <span className="text-slate-300">{fallbackPocDates[0] || formatDateMMDDYY(poc.poc_end_date)}</span>
                              )}
                            </span>

                            <span className={`inline-flex items-center px-1.5 py-0.2 rounded text-[8.5px] font-mono tracking-tight border leading-none ${daysTagClass}`}>
                              {daysStatusText}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {remainingPocsCount > 0 && (
          <button
            onClick={() => onNavigateTab('clients', 'poc_only')}
            className={`w-full text-center font-medium transition-colors cursor-pointer ${
              isOwner || isRc
                ? 'text-[10.5px] py-1.5 mt-2 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 hover:text-slate-900 shadow-2xs flex items-center justify-center gap-1'
                : 'text-[11px] text-sky-400 hover:text-sky-300 py-1 mt-1.5 rounded bg-[#0e1420] md:bg-[#090e1a] border border-slate-700/80 md:border-slate-800/80 hover:border-slate-700'
            }`}
          >
            {isOwner || isRc ? (
              <>
                <span>View All ({remainingPocsCount} more)</span>
                <ArrowRight className="w-2.5 h-2.5" />
              </>
            ) : (
              `+ ${remainingPocsCount} more active clients requiring POC review →`
            )}
          </button>
        )}
      </div>
    );
  };

  const renderSystemHealth = () => (
    <div className={`mobile-ops-shell ${
      isOwner
        ? 'bg-gradient-to-b from-[#142032] via-[#0f1827] to-[#0a101b] md:bg-gradient-to-r md:from-[#1a2b44] md:via-[#142236] md:to-[#0e1726] border border-slate-700/80 hover:border-sky-500/40 rounded-xl px-3 sm:px-4 py-2.5 md:py-2 shadow-[0_4px_16px_rgba(0,0,0,0.4),inset_0_1px_0_0_rgba(255,255,255,0.08)]'
        : 'md:bg-gradient-to-r md:from-[#182436] md:via-[#121c2c] md:to-[#0c1420] border border-slate-700/80 hover:border-cyan-500/40 rounded-xl px-3 sm:px-4 py-2.5 md:py-2 shadow-md shadow-black/40'
    } relative overflow-hidden transition-all`}>
      <div className={`absolute top-0 left-0 right-0 ${isOwner ? 'h-[1.5px] bg-gradient-to-r from-transparent via-sky-400/40 to-transparent pointer-events-none' : 'h-[1.5px] bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent pointer-events-none'}`} />

      {/* MOBILE ONLY (md:hidden): Header + 2x2 Compact Status Grid */}
      <div className="md:hidden space-y-2.5">
        {/* Header: Title + LIVE */}
        <div className="flex items-center justify-between gap-2 text-xs">
          <div className="flex items-center space-x-2 shrink-0">
            <IconBadge icon={ShieldCheck} variant={isOwner ? "blue" : "emerald"} size="xs" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900">
              System & Integrations
            </h3>
          </div>
          <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-semibold flex items-center gap-1 shrink-0 leading-none">
            <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse" />
            LIVE
          </span>
        </div>

        {/* 2x2 Compact Status Grid */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          {/* Cell 1: Primary DB */}
          <div className="flex flex-col justify-center gap-1 p-2 rounded-lg bg-[#142136] border border-slate-700/70 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <span className="text-[10px] uppercase font-mono tracking-wider text-slate-400">Primary DB</span>
            <div>
              {stats?.systemStatus?.database === 'CONNECTED' ? (
                <span className="text-[10px] text-emerald-300 font-mono font-bold bg-emerald-950/70 px-1.5 py-0.5 rounded border border-emerald-500/30 whitespace-nowrap inline-flex items-center gap-1 leading-none">
                  <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse" />
                  Connected
                </span>
              ) : (
                <span className="text-[10px] text-rose-300 font-mono font-bold bg-rose-950/70 px-1.5 py-0.5 rounded border border-rose-500/30 whitespace-nowrap inline-flex leading-none">
                  Disconnected
                </span>
              )}
            </div>
          </div>

          {/* Cell 2: Claim.MD */}
          <div className="flex flex-col justify-center gap-1 p-2 rounded-lg bg-[#142136] border border-slate-700/70 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <span className="text-[10px] uppercase font-mono tracking-wider text-slate-400">Claim.MD</span>
            <div>
              <span className="text-[10px] text-amber-300 font-mono font-bold bg-amber-950/70 px-1.5 py-0.5 rounded border border-amber-500/30 whitespace-nowrap inline-flex leading-none">
                Blocked / Staged
              </span>
            </div>
          </div>

          {/* Cell 3: WellSky */}
          <div className="flex flex-col justify-center gap-1 p-2 rounded-lg bg-[#142136] border border-slate-700/70 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <span className="text-[10px] uppercase font-mono tracking-wider text-slate-400">WellSky</span>
            <div className="truncate">
              <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded border whitespace-nowrap inline-flex leading-none ${
                rawWellSkyTime
                  ? 'text-emerald-300 bg-emerald-950/70 border-emerald-500/30'
                  : 'text-slate-400 bg-slate-900 border-slate-700'
              }`}>
                {rawWellSkyTime ? formatSyncTime(rawWellSkyTime) : 'No Import'}
              </span>
            </div>
          </div>

          {/* Cell 4: RBAC */}
          <div className="flex flex-col justify-center gap-1 p-2 rounded-lg bg-[#142136] border border-slate-700/70 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <span className="text-[10px] uppercase font-mono tracking-wider text-slate-400">RBAC</span>
            <div>
              <span className="text-[10px] text-cyan-300 font-mono font-bold bg-cyan-950/70 px-1.5 py-0.5 rounded border border-cyan-500/30 whitespace-nowrap inline-flex leading-none">
                Active
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* DESKTOP ONLY (hidden md:flex): Preserved Exactly Unchanged */}
      <div className="hidden md:flex flex-wrap items-center justify-between gap-y-2 gap-x-3 text-xs">
        {/* Left: Title + LIVE */}
        <div className="flex items-center space-x-2 shrink-0">
          <IconBadge icon={ShieldCheck} variant={isOwner ? "blue" : "emerald"} size="xs" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900">
            System & Integrations
          </h3>
          <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-semibold flex items-center gap-1 shrink-0 leading-none">
            <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse" />
            LIVE
          </span>
        </div>

        {/* Right: Inline statuses */}
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2.5 text-xs">
          {/* Primary Database */}
          <div className={`flex items-center space-x-1.5 px-2 py-1 rounded ${
            isOwner ? 'bg-[#1c2c44] md:bg-[#101928] border border-slate-600/70 md:border-slate-700/60' : 'bg-[#1c2c44] md:bg-slate-950/60 border border-slate-600/70 md:border-slate-800/70'
          }`}>
            <span className="text-[11px] text-slate-400">Primary DB:</span>
            {stats?.systemStatus?.database === 'CONNECTED' ? (
              <span className="text-[9.5px] text-emerald-300 font-mono font-bold bg-emerald-950/70 px-1.5 py-0.5 rounded border border-emerald-500/30 whitespace-nowrap shrink-0 flex items-center gap-1 leading-none">
                <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse" />
                Connected
              </span>
            ) : (
              <span className="text-[9.5px] text-rose-300 font-mono font-bold bg-rose-950/70 px-1.5 py-0.5 rounded border border-rose-500/30 whitespace-nowrap shrink-0 leading-none">
                Disconnected
              </span>
            )}
          </div>

          <div className="hidden md:block h-3 w-px bg-slate-800" />

          {/* Claim.MD Gateway */}
          <div className={`flex items-center space-x-1.5 px-2 py-1 rounded ${
            isOwner ? 'bg-[#1c2c44] md:bg-[#101928] border border-slate-600/70 md:border-slate-700/60' : 'bg-[#1c2c44] md:bg-slate-950/60 border border-slate-600/70 md:border-slate-800/70'
          }`}>
            <span className="text-[11px] text-slate-400">Claim.MD:</span>
            <span className="text-[9.5px] text-amber-300 font-mono font-bold bg-amber-950/70 px-1.5 py-0.5 rounded border border-amber-500/30 whitespace-nowrap shrink-0 leading-none">
              Blocked / Staged
            </span>
          </div>

          <div className="hidden md:block h-3 w-px bg-slate-800" />

          {/* WellSky Import */}
          <div className={`flex items-center space-x-1.5 px-2 py-1 rounded ${
            isOwner ? 'bg-[#1c2c44] md:bg-[#101928] border border-slate-600/70 md:border-slate-700/60' : 'bg-[#1c2c44] md:bg-slate-950/60 border border-slate-600/70 md:border-slate-800/70'
          }`}>
            <span className="text-[11px] text-slate-400">WellSky:</span>
            <span className={`text-[9.5px] font-mono font-bold px-1.5 py-0.5 rounded border whitespace-nowrap shrink-0 leading-none ${
              rawWellSkyTime
                ? 'text-emerald-300 bg-emerald-950/70 border-emerald-500/30'
                : 'text-slate-400 bg-slate-900 border-slate-700'
            }`}>
              {rawWellSkyTime ? formatSyncTime(rawWellSkyTime) : 'No Import'}
            </span>
          </div>

          <div className="hidden md:block h-3 w-px bg-slate-800" />

          {/* RBAC Enforcement */}
          <div className={`flex items-center space-x-1.5 px-2 py-1 rounded ${
            isOwner ? 'bg-[#1c2c44] md:bg-[#101928] border border-slate-600/70 md:border-slate-700/60' : 'bg-[#1c2c44] md:bg-slate-950/60 border border-slate-600/70 md:border-slate-800/70'
          }`}>
            <span className="text-[11px] text-slate-400">RBAC:</span>
            <span className="text-[9.5px] text-cyan-300 font-mono font-bold bg-cyan-950/70 px-1.5 py-0.5 rounded border border-cyan-500/30 whitespace-nowrap shrink-0 leading-none">
              Active
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderBillingHolds = (inCommandCenter = false) => (
    <div className={`w-full mobile-ops-shell ${
      isOwner
        ? inCommandCenter
          ? 'bg-white border border-slate-200/90 shadow-xs'
          : 'bg-gradient-to-b from-[#142032] via-[#0f1827] to-[#0a101b] md:from-[#0b1322] md:via-[#080e18] md:to-[#050810] border border-slate-700/80 md:border-slate-700/80 shadow-[0_4px_16px_rgba(0,0,0,0.45),0_0_12px_rgba(56,189,248,0.06),inset_0_1px_0_0_rgba(255,255,255,0.08)]'
        : 'md:bg-slate-900/90 border border-slate-600/80 md:border-slate-800/90 shadow-xl shadow-black/40'
    } rounded-xl overflow-hidden flex flex-col relative`}>
      {isOwner && !inCommandCenter && (
        <div className="absolute top-0 left-0 right-0 h-[1.5px] bg-gradient-to-r from-transparent via-sky-400/50 to-transparent shadow-[0_0_8px_rgba(56,189,248,0.25)] pointer-events-none" />
      )}
      <div className={`px-3.5 sm:px-4 py-2 border-b flex justify-between items-center relative overflow-hidden mobile-ops-header ${
        isOwner
          ? 'md:bg-gradient-to-r md:from-[#1a2b44] md:via-[#142236] md:to-[#0e1726] border-b-sky-500/35 shadow-[0_1px_4px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(186,230,253,0.10)]'
          : 'md:bg-gradient-to-r md:from-[#1a2b44] md:via-[#142236] md:to-[#0e1726] border-b-slate-700/80 shadow-[0_1px_4px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.08)]'
      }`}>
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-sky-400/40 to-transparent pointer-events-none" />
        <div className="relative z-10 flex items-center space-x-2">
          <IconBadge icon={AlertTriangle} variant={isOwner ? "blue" : "amber"} size="xs" />
          <div className="flex items-baseline space-x-2">
            <h2 className="text-xs font-bold uppercase tracking-widest text-slate-100">
              Billing Holds
            </h2>
            <span className="text-[10px] text-slate-300 hidden sm:inline">
              Services requiring attention before billing.
            </span>
          </div>
        </div>
        <div className="relative z-10 flex items-center space-x-2">
          <span className="px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 text-[9px] font-bold border border-rose-500/35 font-mono shadow-[0_0_8px_rgba(244,63,94,0.15)] leading-none">
            {stats?.criticalQueue?.length ?? 0} ACTIVE HOLDS
          </span>
          {isOwner && (
            <button
              onClick={() => onNavigateTab('billing', 'Readiness')}
              className="text-[10px] text-sky-400 hover:text-sky-300 font-semibold transition-colors flex items-center gap-0.5 cursor-pointer leading-none ml-1"
            >
              <span>Work Queue</span>
              <ArrowRight className="w-2.5 h-2.5" />
            </button>
          )}
        </div>
      </div>

      <div>
        {(!stats?.criticalQueue || stats.criticalQueue.length === 0) ? (
          <div className="p-6 text-center text-slate-600 text-xs">
            <CheckCircle2 className="w-7 h-7 text-emerald-600 mx-auto mb-1.5" />
            <p className="font-semibold text-slate-800 text-xs">All completed sessions are cleared and billing ready.</p>
            <p className="text-[10px] text-slate-600 mt-0.5">
              Schedule a service or intake a new client to initiate the clinical workflow.
            </p>
          </div>
        ) : (
          <>
            {/* DESKTOP/TABLET TABLE */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead className="bg-gradient-to-b from-[#111928] to-[#0b101b] text-slate-300 text-[9.5px] uppercase border-b border-slate-700/70 tracking-wider font-bold shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]">
                  <tr>
                    <th className="w-[23%] sm:w-[22%] px-3 sm:px-3.5 py-1.5 font-bold">Client / DOS</th>
                    <th className="w-[47%] sm:w-[48%] px-3 sm:px-3.5 py-1.5 font-bold">Hold Reasons</th>
                    <th className="w-[18%] sm:w-[18%] px-3 sm:px-3.5 py-1.5 font-bold">Payer / Plan</th>
                    <th className="w-[12%] px-3 sm:px-3.5 py-1.5 font-bold text-right whitespace-nowrap">Action</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isOwner ? 'divide-slate-700/40' : 'divide-slate-800/60'}`}>
                  {stats.criticalQueue.map((item) => {
                    const rawPayer = item.primary_payer_name || 'Arizona Medicaid';
                    const matchParen = rawPayer.match(/^([^(]+)\s*(\([^)]+\))$/);
                    const matchSlash = !matchParen ? rawPayer.match(/^([^/]+)\s*\/\s*(.+)$/) : null;
                    const payerLine = matchParen ? matchParen[1].trim() : (matchSlash ? matchSlash[1].trim() : rawPayer);
                    const planLine = matchParen ? matchParen[2].trim() : (matchSlash ? `(${matchSlash[2].trim()})` : null);

                    return (
                      <tr
                        key={item.service_id}
                        className={`group relative overflow-hidden transition-all duration-150 border-l-2 border-l-transparent ${
                          isOwner
                            ? 'odd:bg-[#101928] even:bg-[#142034] hover:bg-[#1a283e] hover:border-l-sky-400/80 hover:shadow-[inset_2px_0_8px_rgba(56,189,248,0.08)]'
                            : 'odd:bg-[#101928] even:bg-[#142034] hover:bg-[#1a283e] hover:border-l-emerald-400/80 hover:shadow-[inset_2px_0_8px_rgba(52,211,153,0.08)]'
                        }`}
                      >
                        <td className="w-[23%] sm:w-[22%] px-3 sm:px-3.5 py-1.5 align-middle">
                          <span className={`absolute inset-0 bg-gradient-to-r from-transparent ${
                            isOwner ? 'via-sky-400/[0.04]' : 'via-emerald-400/[0.05]'
                          } to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out pointer-events-none`} />
                          <button
                            onClick={() => onSelectClient(item.client_id)}
                            className={`relative z-10 font-bold text-slate-100 ${isOwner ? 'group-hover:text-sky-200' : 'group-hover:text-cyan-300'} text-left transition-colors text-xs block cursor-pointer leading-tight truncate max-w-[180px]`}
                          >
                            {formatDisplayName(item)}
                          </button>
                          <div className="relative z-10 text-[9.5px] text-slate-400 font-mono mt-0.5 leading-none flex items-center space-x-1.5">
                            <span>DOS: <span className="text-slate-300 font-medium">{item.dos}</span></span>
                            <span className="text-slate-600">•</span>
                            <span>CPT: <span className="text-slate-300 font-medium">{item.service_code}</span></span>
                          </div>
                        </td>
                        <td className="w-[47%] sm:w-[48%] px-3 sm:px-3.5 py-1.5 align-middle">
                          <div className="relative z-10 space-y-1 py-0.5">
                            {(() => {
                              const reasons = item.hold_reasons || [];
                              const primaryReason = reasons[0];
                              const remainingReasons = reasons.slice(1);
                              const isExpanded = expandedHoldServiceIds.has(item.service_id);

                              if (!primaryReason) {
                                return <span className="text-[11px] text-slate-500 italic">No hold reasons specified</span>;
                              }

                              const lower = primaryReason.toLowerCase();
                              const isWarning = lower.includes('signature') || lower.includes('review') || lower.includes('pending') || lower.includes('supervis') || lower.includes('eval');

                              return (
                                <div className="space-y-1">
                                  <div className="flex items-start space-x-1.5 leading-snug">
                                    <span
                                      className={`w-1.5 h-1.5 rounded-full shrink-0 mt-1 ${
                                        isWarning
                                          ? 'bg-amber-500 shadow-[0_0_4px_rgba(245,158,11,0.35)]'
                                          : 'bg-rose-400/80 shadow-[0_0_4px_rgba(244,63,94,0.3)]'
                                      }`}
                                    />
                                    <span className={`text-[11px] font-normal ${isWarning ? 'text-amber-900' : 'text-slate-300'}`}>
                                      {primaryReason}
                                    </span>
                                  </div>

                                  {remainingReasons.length > 0 && (
                                    <div className="pt-0.5 pl-3">
                                      {isExpanded && (
                                        <div className="space-y-1 mb-1.5 border-l border-slate-700/60 pl-2 mt-1">
                                          {remainingReasons.map((reason: string, i: number) => {
                                            const rLower = reason.toLowerCase();
                                            const rWarning = rLower.includes('signature') || rLower.includes('review') || rLower.includes('pending') || rLower.includes('supervis') || rLower.includes('eval');
                                            return (
                                              <div key={i} className="flex items-start space-x-1.5 leading-snug">
                                                <span
                                                  className={`w-1.5 h-1.5 rounded-full shrink-0 mt-1 ${
                                                    rWarning
                                                      ? 'bg-amber-500 shadow-[0_0_4px_rgba(245,158,11,0.35)]'
                                                      : 'bg-rose-400/80 shadow-[0_0_4px_rgba(244,63,94,0.3)]'
                                                  }`}
                                                />
                                                <span className={`text-[11px] font-normal ${rWarning ? 'text-amber-900' : 'text-slate-300'}`}>
                                                  {reason}
                                                </span>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}

                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleHoldExpand(item.service_id);
                                        }}
                                        className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono font-medium text-amber-900 bg-amber-50 hover:bg-amber-100 hover:text-amber-950 border border-amber-200 transition-colors cursor-pointer"
                                      >
                                        {isExpanded ? 'Collapse' : `+${remainingReasons.length} More`}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </td>
                        <td className="w-[18%] sm:w-[18%] px-3 sm:px-3.5 py-1.5 align-middle">
                          <div className="relative z-10 leading-tight">
                            <div className="text-[11px] text-slate-200 font-medium truncate max-w-[170px]" title={rawPayer}>
                              {payerLine}
                            </div>
                            {planLine && (
                              <div className="text-[9.5px] text-slate-400 font-normal truncate max-w-[170px]" title={planLine}>
                                {planLine}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="w-[12%] px-3 sm:px-3.5 py-1.5 align-middle text-right whitespace-nowrap">
                          <button
                            onClick={() => item.service_id && setResolveServiceId(item.service_id)}
                            className="relative z-10 inline-flex items-center space-x-1 px-2.5 py-1 rounded-md bg-gradient-to-b from-[#091b2c] via-[#061422] to-[#040e18] hover:from-[#0c2339] hover:to-[#081827] text-sky-300 hover:text-sky-100 border border-sky-500/40 hover:border-sky-400/70 text-[9.5px] font-semibold transition-all shadow-[0_1px_4px_rgba(0,0,0,0.4),0_0_8px_rgba(56,189,248,0.15),inset_0_1px_0_rgba(255,255,255,0.15)] hover:shadow-[0_0_12px_rgba(56,189,248,0.25),inset_0_1px_0_rgba(255,255,255,0.22)] cursor-pointer group/btn active:scale-98"
                          >
                            <span>Review / Resolve</span>
                            <ArrowRight className="w-2.5 h-2.5 transition-transform group-hover/btn:translate-x-0.5 text-sky-400" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* MOBILE STACKED ROWS */}
            <div className="block md:hidden divide-y divide-slate-600/70 md:divide-slate-700/40">
              {stats.criticalQueue.map((item, idx) => {
                const rawPayer = item.primary_payer_name || 'Arizona Medicaid';
                const matchParen = rawPayer.match(/^([^(]+)\s*(\([^)]+\))$/);
                const matchSlash = !matchParen ? rawPayer.match(/^([^/]+)\s*\/\s*(.+)$/) : null;
                const payerLine = matchParen ? matchParen[1].trim() : (matchSlash ? matchSlash[1].trim() : rawPayer);

                return (
                  <div
                    key={`mobile-cq-${item.service_id}`}
                    className={`p-3 space-y-2 transition-colors ${idx % 2 === 0 ? 'mobile-ops-row-a' : 'mobile-ops-row-b'} hover:from-[#162238] hover:to-[#10192a] active:from-[#1b2b46] active:to-[#142036]`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <button
                          onClick={() => onSelectClient(item.client_id)}
                          className="font-bold text-slate-100 hover:text-sky-300 text-left text-xs transition-colors cursor-pointer leading-tight truncate block"
                        >
                          {formatDisplayName(item)}
                        </button>
                        <div className="text-[10px] text-slate-400 font-mono mt-0.5 flex items-center gap-1.5">
                          <span>DOS: <strong className="text-slate-300">{item.dos}</strong></span>
                          <span className="text-slate-600">•</span>
                          <span>CPT: <strong className="text-slate-300">{item.service_code}</strong></span>
                        </div>
                      </div>
                      <button
                        onClick={() => item.service_id && setResolveServiceId(item.service_id)}
                        className="shrink-0 inline-flex items-center space-x-1 px-2 py-1 rounded-md bg-gradient-to-b from-[#091b2c] to-[#040e18] text-sky-300 border border-sky-500/40 text-[10px] font-semibold"
                      >
                        <span>Review / Resolve</span>
                        <ArrowRight className="w-2.5 h-2.5 text-sky-400" />
                      </button>
                    </div>

                    <div className="text-[10px] text-slate-400 truncate">
                      <span className="text-slate-500">Payer: </span>
                      <span className="text-slate-300">{payerLine}</span>
                    </div>

                    {item.hold_reasons && item.hold_reasons.length > 0 && (
                      <div className="space-y-1 pt-1 border-t border-slate-800/50">
                        {item.hold_reasons.map((reason: string, i: number) => {
                          const lower = reason.toLowerCase();
                          const isWarning = lower.includes('signature') || lower.includes('review') || lower.includes('pending') || lower.includes('supervis') || lower.includes('eval');
                          return (
                            <div key={i} className="flex items-start space-x-1.5 text-[10.5px]">
                              <span
                                className={`w-1.5 h-1.5 rounded-full shrink-0 mt-1 ${
                                  isWarning ? 'bg-amber-500' : 'bg-rose-400'
                                }`}
                              />
                              <span className={isWarning ? 'text-amber-900' : 'text-slate-300'}>
                                {reason}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Workspace Header & Command Surface */}
      {isReferralCoordinator ? (
        <>
          {/* Mobile Header for Referral Coordinator: Canonical Steel-to-Navy Shell (md:hidden) */}
          <div className="md:hidden relative overflow-hidden rounded-2xl border mobile-header-shell px-3.5 py-3">
            <div className="absolute top-0 right-0 w-96 h-full bg-gradient-to-l from-sky-500/5 via-indigo-500/5 to-transparent pointer-events-none" />
            <div className="absolute top-0 left-0 right-0 h-[1.5px] bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent pointer-events-none" />
            <div className="relative z-10 flex items-center justify-between gap-3">
              <div className="flex items-center space-x-2.5 shrink-0">
                <h1 className="text-lg font-bold text-white tracking-tight">
                  Dashboard
                </h1>
                <span className="inline-flex items-center gap-1.5 text-[10px] font-mono font-semibold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/25 shadow-xs">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span>{stats?.systemStatus?.database === 'CONNECTED' ? 'LIVE • CLOUD SQL' : 'LIVE'}</span>
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  playPulseSearchBeacon();
                  onOpenAIWithPrompt('');
                }}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-[#334155] shadow-sm hover:shadow active:scale-[0.98] transition-all cursor-pointer select-none whitespace-nowrap shrink-0"
                title="AI Assist"
              >
                <img src="/brand/canonical/bloom-mark-micro.svg" alt="Bloom" className="w-4.5 h-4.5 shrink-0 object-contain brightness-0 saturate-100 opacity-[0.72]" />
                <span className="text-xs font-semibold text-[#334155] tracking-tight">AI Assist</span>
              </button>
            </div>
          </div>

          {/* Desktop/Tablet Header for Referral Coordinator: Canonical Navy Shell */}
          <div className="hidden md:block relative overflow-hidden rounded-2xl border border-slate-700/80 desktop-header-shell px-6 py-4 shadow-[0_4px_16px_rgba(0,0,0,0.4),inset_0_1px_0_0_rgba(255,255,255,0.12)]">
            {/* Subtle specular top hairline */}
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-slate-400/30 to-transparent pointer-events-none" />
            <div className="absolute top-0 right-0 w-96 h-full bg-gradient-to-l from-sky-500/5 via-indigo-500/5 to-transparent pointer-events-none" />

            <div className="relative z-10 flex flex-col items-center justify-center text-center">
              <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                Dashboard
              </h1>
              <div className="mt-1.5 flex items-center justify-center">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-mono font-semibold px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/25 shadow-xs">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  {stats?.systemStatus?.database === 'CONNECTED' ? 'LIVE • CLOUD SQL' : 'LIVE • CLOUD SQL'}
                </span>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Mobile Header for Owner / Operational Dashboard: Canonical Steel-to-Navy Shell (md:hidden) */}
          <div className="md:hidden relative overflow-hidden rounded-2xl border mobile-header-shell px-3.5 py-3">
            <div className="absolute top-0 right-0 w-96 h-full bg-gradient-to-l from-sky-500/5 via-indigo-500/5 to-transparent pointer-events-none" />
            <div className="absolute top-0 left-0 right-0 h-[1.5px] bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent pointer-events-none" />
            <div className="relative z-10 flex items-center justify-between gap-3">
              <div className="flex items-center space-x-2.5 shrink-0">
                <h1 className="text-lg font-bold text-white tracking-tight">
                  Dashboard
                </h1>
                <span className="inline-flex items-center gap-1.5 text-[10px] font-mono font-semibold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/25 shadow-xs">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span>{stats?.systemStatus?.database === 'CONNECTED' ? 'LIVE • CLOUD SQL' : 'LIVE'}</span>
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  playPulseSearchBeacon();
                  onOpenAIWithPrompt('');
                }}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-slate-600/80 hover:border-sky-400/60 bg-gradient-to-b from-[#1d2d46] to-[#121c2c] hover:from-[#253958] hover:to-[#172438] text-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.35),inset_0_1px_0_0_rgba(255,255,255,0.14)] hover:shadow-[0_0_8px_rgba(56,189,248,0.2)] active:scale-[0.98] transition-all cursor-pointer select-none whitespace-nowrap shrink-0"
                title="AI Assist"
              >
                <img src="/brand/canonical/bloom-mark-micro.svg" alt="Bloom" className="w-4.5 h-4.5 shrink-0 object-contain" />
                <span className="text-xs font-semibold text-slate-100 tracking-tight">AI Assist</span>
              </button>
            </div>
          </div>

          {/* Desktop/Tablet Header for Owner: Canonical Navy Shell */}
          <div className="hidden md:block relative overflow-hidden rounded-2xl border border-slate-700/80 desktop-header-shell px-6 py-4 shadow-[0_4px_16px_rgba(0,0,0,0.4),inset_0_1px_0_0_rgba(255,255,255,0.12)]">
            {/* Subtle specular top hairline */}
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-slate-400/30 to-transparent pointer-events-none" />
            <div className="absolute top-0 right-0 w-96 h-full bg-gradient-to-l from-sky-500/5 via-indigo-500/5 to-transparent pointer-events-none" />

            <div className="relative z-10 flex flex-col items-center justify-center text-center">
              <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                Dashboard
              </h1>
              <div className="mt-1.5 flex items-center justify-center">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-mono font-semibold px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/25 shadow-xs">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  {stats?.systemStatus?.database === 'CONNECTED' ? 'LIVE • CLOUD SQL' : 'LIVE'}
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Metric Cards Row & Operational Detail Sections */}
      {isReferralCoordinator ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-2.5">
            {/* Active Clients Card */}
            <div
              onClick={() => onNavigateTab('clients')}
              className="mobile-ops-kdi bg-white hover:bg-slate-50/80 border border-slate-200/90 hover:border-slate-300 rounded-lg sm:rounded-xl px-2 py-1.5 sm:px-3.5 sm:py-2 shadow-xs relative overflow-hidden transition-all group cursor-pointer flex flex-col justify-between min-w-0"
            >
              <div className="flex items-center justify-between text-[8px] sm:text-[9.5px] uppercase tracking-wider text-slate-700 font-bold leading-none min-w-0">
                <span className="truncate">Active Clients</span>
                <div className="hidden sm:block shrink-0">
                  <IconBadge icon={Users} variant="sky" size="xs" />
                </div>
              </div>
              <div className="text-base sm:text-2xl font-extrabold mt-0.5 sm:mt-1 text-slate-900 tracking-tight leading-none">{stats?.activeClients ?? 0}</div>
              <div className="text-[7.5px] sm:text-[10px] text-slate-600 mt-0.5 sm:mt-1 flex items-center space-x-0.5 sm:space-x-1 font-medium truncate leading-tight">
                <TrendingUp className="w-2.5 h-2.5 sm:w-3 sm:h-3 shrink-0 text-emerald-600" />
                <span className="truncate">Agency-Wide Roster</span>
              </div>
            </div>

            {/* Pending Signatures Card */}
            <div
              onClick={() => onNavigateTab('clinical')}
              className="mobile-ops-kdi bg-white hover:bg-slate-50/80 border border-amber-200/80 hover:border-amber-300 rounded-lg sm:rounded-xl px-2 py-1.5 sm:px-3.5 sm:py-2 shadow-xs relative overflow-hidden transition-all group cursor-pointer flex flex-col justify-between min-w-0"
            >
              <div className="flex items-center justify-between text-[8px] sm:text-[9.5px] uppercase tracking-wider text-slate-700 font-bold leading-none min-w-0">
                <span className="truncate">Pending Signatures</span>
                <div className="hidden sm:block shrink-0">
                  <IconBadge icon={Clock} variant="amber" size="xs" />
                </div>
              </div>
              <div className="text-base sm:text-2xl font-extrabold mt-0.5 sm:mt-1 text-amber-800 tracking-tight leading-none">{stats?.pendingSignatures ?? 0}</div>
              <div className="text-[7.5px] sm:text-[10px] text-slate-600 mt-0.5 sm:mt-1 font-medium truncate leading-tight" title="Document & Guardian Holds">
                <span className="truncate">Document & Guardian Holds</span>
              </div>
            </div>

            {/* Expiring Authorizations Card */}
            <div
              onClick={() => onNavigateTab('clients', 'auth_only')}
              className="mobile-ops-kdi bg-white hover:bg-slate-50/80 border border-slate-200/90 hover:border-amber-300/80 rounded-lg sm:rounded-xl px-2 py-1.5 sm:px-3.5 sm:py-2 shadow-2xs relative overflow-hidden transition-all group cursor-pointer flex flex-col justify-between min-w-0"
            >
              <div className="flex items-center justify-between text-[8px] sm:text-[9.5px] uppercase tracking-wider text-slate-900 font-bold leading-none min-w-0">
                <span className="truncate">Expiring Authorizations</span>
                <div className="hidden sm:block shrink-0">
                  <IconBadge icon={FileCheck2} variant="amber" size="xs" />
                </div>
              </div>
              <div className="text-base sm:text-2xl font-extrabold mt-0.5 sm:mt-1 text-slate-900 tracking-tight leading-none font-mono">{activeExpiringAuths.length}</div>
              <div className="text-[7.5px] sm:text-[10px] text-slate-600 mt-0.5 sm:mt-1 font-medium truncate leading-tight" title="14-Day Window & Low Units">
                <span className="truncate">14-Day Window & Low Units</span>
              </div>
            </div>

            {/* Expiring Plans of Care Card */}
            <div
              onClick={() => onNavigateTab('clients', 'poc_only')}
              className="mobile-ops-kdi bg-white hover:bg-slate-50/80 border border-slate-200/90 hover:border-slate-300 rounded-lg sm:rounded-xl px-2 py-1.5 sm:px-3.5 sm:py-2 shadow-2xs relative overflow-hidden transition-all group cursor-pointer flex flex-col justify-between min-w-0"
            >
              <div className="flex items-center justify-between text-[8px] sm:text-[9.5px] uppercase tracking-wider text-slate-900 font-bold leading-none min-w-0">
                <span className="truncate">Expiring Plans of Care</span>
                <div className="hidden sm:block shrink-0">
                  <IconBadge icon={Stethoscope} variant="blue" size="xs" />
                </div>
              </div>
              <div className="text-base sm:text-2xl font-extrabold mt-0.5 sm:mt-1 text-slate-900 tracking-tight leading-none font-mono">{rcExpiringPocs.length}</div>
              <div className="text-[7.5px] sm:text-[10px] text-slate-600 mt-0.5 sm:mt-1 font-medium truncate leading-tight" title="30-Day Window">
                30-Day Window
              </div>
            </div>
          </div>

          {/* Operational Expirations: Expiring Authorizations & Expiring Plans of Care */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5 sm:gap-4 items-stretch">
            <div className="min-w-0 flex flex-col">
              {renderExpiringAuths(true)}
            </div>
            <div className="min-w-0 flex flex-col">
              {renderExpiringPocs(true)}
            </div>
          </div>
        </>
      ) : isOwner ? (
        /* ========================================================================= */
        /* OWNER DESKTOP & MOBILE SURFACE                                            */
        /* ========================================================================= */
        <>
          {/* MOBILE OWNER VIEW (md:hidden) — Preserves full mobile ergonomics */}
          <div className="md:hidden space-y-3.5">
            {/* 3-Card KPI Strip */}
            <div className="grid grid-cols-1 gap-3">
              {/* Active Clients Card */}
              <div
                onClick={() => onNavigateTab('clients')}
                className="mobile-ops-kdi bg-gradient-to-b from-[#1a283f] via-[#132032] to-[#0d1624] border border-slate-700/80 rounded-xl px-4 py-2.5 relative overflow-hidden transition-all group cursor-pointer"
              >
                <div className="absolute top-0 left-0 right-0 h-[1.5px] bg-gradient-to-r from-transparent via-emerald-400/40 to-transparent pointer-events-none" />
                <div className="relative z-10 flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-800 font-bold">
                  <span>Active Clients</span>
                  <IconBadge icon={Users} variant="sky" size="xs" />
                </div>
                <div className="relative z-10 flex items-baseline justify-between mt-1">
                  <div className="text-2xl font-extrabold text-slate-900 tracking-tight leading-tight">{stats?.activeClients ?? 0}</div>
                  <div className="text-[11px] text-slate-600 flex items-center space-x-1 font-medium">
                    <TrendingUp className="w-3 h-3 shrink-0 text-emerald-600" />
                    <span>Active Caseload</span>
                  </div>
                </div>
              </div>

              {/* Pending Signatures Card */}
              <div
                onClick={() => onNavigateTab('clinical')}
                className="mobile-ops-kdi bg-gradient-to-b from-[#1a283f] via-[#132032] to-[#0d1624] border border-slate-700/80 rounded-xl px-4 py-2.5 relative overflow-hidden transition-all group cursor-pointer"
              >
                <div className="absolute top-0 left-0 right-0 h-[1.5px] bg-gradient-to-r from-transparent via-amber-400/40 to-transparent pointer-events-none" />
                <div className="relative z-10 flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-900 font-bold">
                  <span>Pending Signatures</span>
                  <IconBadge icon={Clock} variant="amber" size="xs" />
                </div>
                <div className="relative z-10 flex items-baseline justify-between mt-1">
                  <div className="text-2xl font-extrabold text-amber-800 tracking-tight leading-tight">{stats?.pendingSignatures ?? 0}</div>
                  <div className="text-[11px] text-slate-600 font-medium">Clinician, Guardian & Sup Holds</div>
                </div>
              </div>

              {/* Unbilled Revenue Card */}
              <div
                onClick={() => onNavigateTab('billing', 'Readiness')}
                className="mobile-ops-kdi bg-gradient-to-b from-[#1a283f] via-[#132032] to-[#0d1624] border border-slate-700/80 rounded-xl px-4 py-2.5 relative overflow-hidden transition-all group cursor-pointer"
              >
                <div className="absolute top-0 left-0 right-0 h-[1.5px] bg-gradient-to-r from-transparent via-emerald-400/40 to-transparent pointer-events-none" />
                <div className="relative z-10 flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-900 font-bold">
                  <span>Unbilled Revenue</span>
                  <IconBadge icon={CircleDollarSign} variant="emerald" size="xs" />
                </div>
                <div className="relative z-10 flex items-baseline justify-between mt-1">
                  <div className="text-2xl font-extrabold text-slate-900 tracking-tight leading-tight">
                    ${Number(stats?.unbilledRevenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="text-[11px] text-slate-600 font-medium">Ready to Bill Services</div>
                </div>
              </div>
            </div>

            {/* Mobile Expirations */}
            <div className="space-y-3.5">
              <div className="min-w-0 flex flex-col">
                {renderExpiringAuths(false)}
              </div>
              <div className="min-w-0 flex flex-col">
                {renderExpiringPocs(false)}
              </div>
            </div>

            {/* Mobile System Health */}
            {renderSystemHealth()}

            {/* Mobile Billing Holds */}
            {renderBillingHolds(false)}
          </div>

          {/* DESKTOP OWNER VIEW (hidden md:block) — Billing First Hierarchy */}
          <div className="hidden md:block space-y-4">
            {/* Top-Level Billing Command Center */}
            <div className="relative overflow-hidden rounded-2xl border border-slate-200/90 bg-white p-4 sm:p-5 shadow-sm space-y-4">
              {/* Command Center Header: 3 Ordered Rows for Clean Desktop & Small-Desktop Flow */}
              <div className="rounded-xl bg-gradient-to-r from-[#18263c] via-[#142034] to-[#0f1726] border border-slate-700/80 p-3.5 sm:p-4 shadow-xs space-y-3">
                {/* Row 1: Full Title Area, Icon, and Primary Revenue Surface Badge */}
                <div className="flex items-center justify-between gap-3 min-w-0">
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className="p-2 rounded-xl bg-slate-800/80 border border-slate-700 shadow-xs shrink-0">
                      <CircleDollarSign className="w-5 h-5 text-sky-300" />
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-2.5 min-w-0">
                      <h2 className="text-base sm:text-lg font-bold text-white tracking-tight whitespace-nowrap">
                        Billing Command Center
                      </h2>
                      <span className="px-2 py-0.5 rounded text-[9.5px] font-mono font-bold bg-slate-800 text-slate-200 border border-slate-700 shadow-xs leading-none uppercase shrink-0">
                        Primary Revenue Surface
                      </span>
                    </div>
                  </div>
                </div>

                {/* Row 2: Top Billing Navigation Buttons Spread Evenly Across Available Width */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-2.5 w-full">
                  <button
                    type="button"
                    onClick={() => onNavigateTab('billing', 'Readiness')}
                    className="w-full h-full min-h-[38px] inline-flex items-center justify-center space-x-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 hover:border-slate-600 text-xs font-semibold shadow-xs active:scale-[0.98] transition-all cursor-pointer truncate"
                    title="Open Billing Work Queue and Unbilled Services"
                  >
                    <FileSpreadsheet className="w-3.5 h-3.5 text-sky-300 shrink-0" />
                    <span className="truncate">Work Queue</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => onNavigateTab('billing', 'claims')}
                    className="w-full h-full min-h-[38px] inline-flex items-center justify-center space-x-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 hover:border-slate-600 text-xs font-semibold shadow-xs active:scale-[0.98] transition-all cursor-pointer truncate"
                    title="Open Claims Workspace and EDI 837 Batch Export"
                  >
                    <Layers className="w-3.5 h-3.5 text-sky-300 shrink-0" />
                    <span className="truncate">Claims & EDI 837</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => onNavigateTab('billing', 'remittances')}
                    className="w-full h-full min-h-[38px] inline-flex items-center justify-center space-x-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 hover:border-slate-600 text-xs font-semibold shadow-xs active:scale-[0.98] transition-all cursor-pointer truncate"
                    title="Upload Multi-Client Scanned EOB Batches and Reconcile Remittances"
                  >
                    <UploadCloud className="w-3.5 h-3.5 text-emerald-300 shrink-0" />
                    <span className="truncate">Upload Multi-Client EOB</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => onNavigateTab('billing', 'payments')}
                    className="w-full h-full min-h-[38px] inline-flex items-center justify-center space-x-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 hover:border-slate-600 text-xs font-semibold shadow-xs active:scale-[0.98] transition-all cursor-pointer truncate"
                    title="Open Accounts Receivable, Aging Buckets & Client Ledger"
                  >
                    <CreditCard className="w-3.5 h-3.5 text-sky-300 shrink-0" />
                    <span className="truncate">AR & Ledger</span>
                  </button>
                </div>

                {/* Row 3: Descriptive Billing Text Presented as Evenly Spaced, Easy-to-Scan Columns */}
                <div className="w-full pt-0.5">
                  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2 text-[11px] font-medium text-slate-300">
                    <div className="flex items-center space-x-1.5 px-2.5 py-1.5 rounded-lg bg-slate-900/60 border border-slate-800/80 justify-center text-center">
                      <span className="w-1.5 h-1.5 rounded-full bg-sky-400 shrink-0" />
                      <span className="truncate">End-to-end revenue cycle</span>
                    </div>
                    <div className="flex items-center space-x-1.5 px-2.5 py-1.5 rounded-lg bg-slate-900/60 border border-slate-800/80 justify-center text-center">
                      <span className="w-1.5 h-1.5 rounded-full bg-sky-400 shrink-0" />
                      <span className="truncate">Claims batch preparation</span>
                    </div>
                    <div className="flex items-center space-x-1.5 px-2.5 py-1.5 rounded-lg bg-slate-900/60 border border-slate-800/80 justify-center text-center">
                      <span className="w-1.5 h-1.5 rounded-full bg-sky-400 shrink-0" />
                      <span className="truncate">Clearinghouse review</span>
                    </div>
                    <div className="flex items-center space-x-1.5 px-2.5 py-1.5 rounded-lg bg-slate-900/60 border border-slate-800/80 justify-center text-center">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                      <span className="truncate">Multi-client remittances</span>
                    </div>
                    <div className="col-span-2 md:col-span-1 flex items-center space-x-1.5 px-2.5 py-1.5 rounded-lg bg-slate-900/60 border border-slate-800/80 justify-center text-center">
                      <span className="w-1.5 h-1.5 rounded-full bg-sky-400 shrink-0" />
                      <span className="truncate">Accounts receivable</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Billing Quick Actions Row — Compact Owner Desktop Launcher */}
              <div className="rounded-xl border border-slate-200/90 bg-slate-50/70 p-3 sm:p-3.5 shadow-xs space-y-2.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-800 flex items-center gap-1.5">
                      <Zap className="w-3.5 h-3.5 text-amber-500" />
                      <span>Billing Quick Actions</span>
                    </span>
                    <span className="text-[10px] text-slate-500 border-l border-slate-300 pl-2 hidden sm:inline">
                      Direct launch to staging & reconciliation workspaces
                    </span>
                  </div>
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-mono font-medium bg-amber-50 text-amber-800 border border-amber-200/80">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                    <span>Manual review required</span>
                  </span>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-2.5">
                  {/* Button 1: WellSky Import */}
                  <button
                    type="button"
                    onClick={() => onNavigateTab('authorizations', 'wellsky')}
                    className="flex flex-col justify-between text-left p-2.5 sm:p-3 rounded-lg bg-white hover:bg-slate-50/80 border border-slate-200/90 hover:border-slate-300 shadow-xs transition-all cursor-pointer group active:scale-[0.98]"
                    title="Open WellSky Report 11281 CSV Import & Staging (Manual Review Required)"
                  >
                    <div className="flex items-center justify-between w-full mb-1.5">
                      <span className="p-1 rounded-md bg-sky-50 border border-sky-200 text-sky-700">
                        <FileSpreadsheet className="w-3.5 h-3.5" />
                      </span>
                      <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-700 group-hover:translate-x-0.5 transition-all" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-slate-900 group-hover:text-slate-950 transition-colors">
                        WellSky Import
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5 leading-snug">
                        Report 11281 CSV · Staging preview
                      </div>
                    </div>
                  </button>

                  {/* Button 2: Upload Multi-Client EOB */}
                  <button
                    type="button"
                    onClick={() => onNavigateTab('billing', 'remittances')}
                    className="flex flex-col justify-between text-left p-2.5 sm:p-3 rounded-lg bg-white hover:bg-slate-50/80 border border-slate-200/90 hover:border-slate-300 shadow-xs transition-all cursor-pointer group active:scale-[0.98]"
                    title="Open Multi-Client Scanned EOB / 835 Remittance Workflow (Manual Review Required)"
                  >
                    <div className="flex items-center justify-between w-full mb-1.5">
                      <span className="p-1 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700">
                        <UploadCloud className="w-3.5 h-3.5" />
                      </span>
                      <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-700 group-hover:translate-x-0.5 transition-all" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-slate-900 group-hover:text-slate-950 transition-colors">
                        Upload Multi-Client EOB
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5 leading-snug">
                        Multi-client PDF/835 · Allocation audit
                      </div>
                    </div>
                  </button>

                  {/* Button 3: Upload Authorization */}
                  <button
                    type="button"
                    onClick={() => onNavigateTab('authorizations', 'upload')}
                    className="flex flex-col justify-between text-left p-2.5 sm:p-3 rounded-lg bg-white hover:bg-slate-50/80 border border-slate-200/90 hover:border-slate-300 shadow-xs transition-all cursor-pointer group active:scale-[0.98]"
                    title="Open Authorization Upload & Entry Workspace (Manual Review Required)"
                  >
                    <div className="flex items-center justify-between w-full mb-1.5">
                      <span className="p-1 rounded-md bg-blue-50 border border-blue-200 text-blue-700">
                        <ShieldCheck className="w-3.5 h-3.5" />
                      </span>
                      <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-700 group-hover:translate-x-0.5 transition-all" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-slate-900 group-hover:text-slate-950 transition-colors">
                        Upload Authorization
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5 leading-snug">
                        Manual entry & verification · Units audit
                      </div>
                    </div>
                  </button>

                  {/* Button 4: Claims & EDI 837 */}
                  <button
                    type="button"
                    onClick={() => onNavigateTab('billing', 'claims')}
                    className="flex flex-col justify-between text-left p-2.5 sm:p-3 rounded-lg bg-white hover:bg-slate-50/80 border border-slate-200/90 hover:border-slate-300 shadow-xs transition-all cursor-pointer group active:scale-[0.98]"
                    title="Open Claims Workspace and EDI 837 Batch Export (Manual Review Required)"
                  >
                    <div className="flex items-center justify-between w-full mb-1.5">
                      <span className="p-1 rounded-md bg-amber-50 border border-amber-200 text-amber-700">
                        <Layers className="w-3.5 h-3.5" />
                      </span>
                      <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-700 group-hover:translate-x-0.5 transition-all" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-slate-900 group-hover:text-slate-950 transition-colors">
                        Claims & EDI 837
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5 leading-snug">
                        Batch preparation · 837 export staging
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Core Revenue & Work Queue KPI Strip */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {/* Unbilled Revenue Card */}
                <div
                  onClick={() => onNavigateTab('billing', 'Readiness')}
                  className="bg-white hover:bg-slate-50/80 border border-slate-200/90 hover:border-emerald-300/80 rounded-xl p-3.5 shadow-xs relative overflow-hidden transition-all group cursor-pointer"
                >
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-700 font-bold">
                    <span>Unbilled Revenue</span>
                    <IconBadge icon={CircleDollarSign} variant="emerald" size="xs" />
                  </div>
                  <div className="mt-1 flex items-baseline justify-between">
                    <div className="text-2xl lg:text-3xl font-extrabold text-slate-900 tracking-tight leading-none font-mono">
                      ${Number(stats?.unbilledRevenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                  <div className="mt-1.5 flex items-center justify-between text-[11px]">
                    <span className="text-emerald-700 font-medium">Ready to Bill Services</span>
                    <span className="text-slate-600 group-hover:text-slate-900 font-medium flex items-center gap-0.5">
                      Review Queue <ArrowRight className="w-2.5 h-2.5 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </div>

                {/* Active Billing Holds Card */}
                <div
                  onClick={() => onNavigateTab('billing', 'Readiness')}
                  className="bg-white hover:bg-slate-50/80 border border-slate-200/90 hover:border-rose-300/80 rounded-xl p-3.5 shadow-xs relative overflow-hidden transition-all group cursor-pointer"
                >
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-700 font-bold">
                    <span>Active Billing Holds</span>
                    <IconBadge icon={AlertTriangle} variant="rose" size="xs" />
                  </div>
                  <div className="mt-1 flex items-baseline justify-between">
                    <div className="text-2xl lg:text-3xl font-extrabold text-rose-700 tracking-tight leading-none font-mono">
                      {stats?.criticalQueue?.length ?? 0}
                    </div>
                  </div>
                  <div className="mt-1.5 flex items-center justify-between text-[11px]">
                    <span className="text-rose-600 font-medium">
                      {(stats?.criticalQueue?.length ?? 0) === 0 ? 'All Sessions Cleared' : 'Blocked Services'}
                    </span>
                    <span className="text-slate-600 group-hover:text-rose-800 font-medium flex items-center gap-0.5">
                      View Holds <ArrowRight className="w-2.5 h-2.5 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </div>

                {/* Pending Signatures Card */}
                <div
                  onClick={() => onNavigateTab('clinical')}
                  className="bg-white hover:bg-slate-50/80 border border-slate-200/90 hover:border-amber-300/80 rounded-xl p-3.5 shadow-xs relative overflow-hidden transition-all group cursor-pointer"
                >
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-700 font-bold">
                    <span>Pending Signatures</span>
                    <IconBadge icon={Clock} variant="amber" size="xs" />
                  </div>
                  <div className="mt-1 flex items-baseline justify-between">
                    <div className="text-2xl lg:text-3xl font-extrabold text-amber-700 tracking-tight leading-none font-mono">
                      {stats?.pendingSignatures ?? 0}
                    </div>
                  </div>
                  <div className="mt-1.5 flex items-center justify-between text-[11px]">
                    <span className="text-slate-600 font-medium">Clinical & Guardian Holds</span>
                    <span className="text-slate-600 group-hover:text-amber-800 font-medium flex items-center gap-0.5">
                      Review Docs <ArrowRight className="w-2.5 h-2.5 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </div>

                {/* Active Clients Card */}
                <div
                  onClick={() => onNavigateTab('clients')}
                  className="bg-white hover:bg-slate-50/80 border border-slate-200/90 hover:border-slate-300 rounded-xl p-3.5 shadow-xs relative overflow-hidden transition-all group cursor-pointer"
                >
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-800 font-bold">
                    <span>Active Clients</span>
                    <IconBadge icon={Users} variant="sky" size="xs" />
                  </div>
                  <div className="mt-1 flex items-baseline justify-between">
                    <div className="text-2xl lg:text-3xl font-extrabold text-slate-900 tracking-tight leading-none font-mono">
                      {stats?.activeClients ?? 0}
                    </div>
                  </div>
                  <div className="mt-1.5 flex items-center justify-between text-[11px]">
                    <span className="text-slate-600 font-medium flex items-center space-x-1">
                      <TrendingUp className="w-3 h-3 shrink-0 text-emerald-600" />
                      <span>Agency-Wide Roster</span>
                    </span>
                    <span className="text-slate-600 group-hover:text-slate-900 font-medium flex items-center gap-0.5">
                      View Roster <ArrowRight className="w-2.5 h-2.5 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </div>
              </div>

              {/* Owner Billing Workflow Hub: 6 Structured Operational Cards */}
              <div className="pt-2">
                <div className="flex items-center justify-between pb-2 mb-2.5 border-b border-slate-200">
                  <div className="text-[11px] font-bold uppercase tracking-widest text-slate-700 flex items-center gap-1.5">
                    <Layers className="w-3.5 h-3.5 text-slate-600" />
                    <span>Revenue Cycle Navigation & Workflows</span>
                  </div>
                  <span className="text-[10px] text-slate-600">
                    Select a workflow to open the live workspace
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
                  {/* 1. Batch Preparation & Manual-Upload Readiness */}
                  <div
                    onClick={() => onNavigateTab('billing', 'Readiness')}
                    className="p-3 rounded-xl bg-white hover:bg-slate-50/80 border border-slate-200/90 hover:border-slate-300 shadow-xs transition-all cursor-pointer group flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <span className="text-[10px] font-mono font-bold text-slate-700 uppercase tracking-wider bg-slate-100 border border-slate-200/80 px-2 py-0.5 rounded leading-none">
                          Batch Preparation
                        </span>
                        <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-700 group-hover:translate-x-0.5 transition-all" />
                      </div>
                      <h3 className="text-xs font-bold text-slate-900 group-hover:text-slate-950 transition-colors">
                        Batch Prep & Upload Readiness
                      </h3>
                      <p className="text-[11px] text-slate-600 mt-1 leading-snug">
                        Audit verified sessions, confirm CPT/ICD-10 coding, and stage clean encounters for batch creation.
                      </p>
                    </div>
                    <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-600">
                      <span>Target: Billing Readiness Tab</span>
                      <span className="text-slate-700 group-hover:text-slate-950 font-semibold group-hover:underline">Open Queue →</span>
                    </div>
                  </div>

                  {/* 2. Multi-Client EOB Uploads & Remittances */}
                  <div
                    onClick={() => onNavigateTab('billing', 'remittances')}
                    className="p-3 rounded-xl bg-white hover:bg-slate-50/80 border border-slate-200/90 hover:border-slate-300 shadow-xs transition-all cursor-pointer group flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <span className="text-[10px] font-mono font-bold text-emerald-800 uppercase tracking-wider bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded leading-none">
                          EOB / ERA Ingestion
                        </span>
                        <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-700 group-hover:translate-x-0.5 transition-all" />
                      </div>
                      <h3 className="text-xs font-bold text-slate-900 group-hover:text-slate-950 transition-colors">
                        Multi-Client EOB Uploads & Remittances
                      </h3>
                      <p className="text-[11px] text-slate-600 mt-1 leading-snug">
                        Upload multi-client scanned EOB files, slice page ranges, allocate lines, and reconcile 835 ERAs.
                      </p>
                    </div>
                    <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-600">
                      <span>Target: Remittances Tab</span>
                      <span className="text-emerald-700 group-hover:text-emerald-900 font-semibold group-hover:underline">Upload EOB →</span>
                    </div>
                  </div>

                  {/* 3. Clearinghouse Responses & Rejections */}
                  <div
                    onClick={() => onNavigateTab('billing', 'claims')}
                    className="p-3 rounded-xl bg-white hover:bg-slate-50/80 border border-slate-200/90 hover:border-slate-300 shadow-xs transition-all cursor-pointer group flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <span className="text-[10px] font-mono font-bold text-amber-800 uppercase tracking-wider bg-amber-50 border border-amber-200 px-2 py-0.5 rounded leading-none">
                          Clearinghouse Status
                        </span>
                        <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-700 group-hover:translate-x-0.5 transition-all" />
                      </div>
                      <h3 className="text-xs font-bold text-slate-900 group-hover:text-slate-950 transition-colors">
                        Clearinghouse Responses & Rejections
                      </h3>
                      <p className="text-[11px] text-slate-600 mt-1 leading-snug">
                        Inspect Claim.MD 999/277CA acceptance reports, resolve payer rejections, and verify batch transmission.
                      </p>
                    </div>
                    <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-600">
                      <span>Target: Claims Workspace</span>
                      <span className="text-amber-700 group-hover:text-amber-900 font-semibold group-hover:underline">View Status →</span>
                    </div>
                  </div>

                  {/* 4. Claims Workspace & Batch 837 Export */}
                  <div
                    onClick={() => onNavigateTab('billing', 'claims')}
                    className="p-3 rounded-xl bg-white hover:bg-slate-50/80 border border-slate-200/90 hover:border-slate-300 shadow-xs transition-all cursor-pointer group flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <span className="text-[10px] font-mono font-bold text-blue-800 uppercase tracking-wider bg-blue-50 border border-blue-200 px-2 py-0.5 rounded leading-none">
                          Claims Workspace
                        </span>
                        <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-700 group-hover:translate-x-0.5 transition-all" />
                      </div>
                      <h3 className="text-xs font-bold text-slate-900 group-hover:text-slate-950 transition-colors">
                        Claims Workspace & EDI 837 Export
                      </h3>
                      <p className="text-[11px] text-slate-600 mt-1 leading-snug">
                        Filter claims across AHCCCS and commercial plans, export 837 EDI files, and advance adjudication stages.
                      </p>
                    </div>
                    <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-600">
                      <span>Target: Claims Workspace</span>
                      <span className="text-blue-700 group-hover:text-blue-900 font-semibold group-hover:underline">Open Claims →</span>
                    </div>
                  </div>

                  {/* 5. Accounts Receivable & Aging Ledger */}
                  <div
                    onClick={() => onNavigateTab('billing', 'payments')}
                    className="p-3 rounded-xl bg-white hover:bg-slate-50/80 border border-slate-200/90 hover:border-slate-300 shadow-xs transition-all cursor-pointer group flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <span className="text-[10px] font-mono font-bold text-slate-700 uppercase tracking-wider bg-slate-100 border border-slate-200/80 px-2 py-0.5 rounded leading-none">
                          Accounts Receivable
                        </span>
                        <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-700 group-hover:translate-x-0.5 transition-all" />
                      </div>
                      <h3 className="text-xs font-bold text-slate-900 group-hover:text-slate-950 transition-colors">
                        Accounts Receivable & Aging Ledger
                      </h3>
                      <p className="text-[11px] text-slate-600 mt-1 leading-snug">
                        Track aging buckets (30/60/90+ days), manage client ledgers, record patient co-pays, and post payments.
                      </p>
                    </div>
                    <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-600">
                      <span>Target: Payments & Ledger Tab</span>
                      <span className="text-slate-700 group-hover:text-slate-950 font-semibold group-hover:underline">View AR →</span>
                    </div>
                  </div>

                  {/* 6. Weekly & Monthly Billing Review */}
                  <div
                    onClick={() => onNavigateTab('billing', 'Readiness')}
                    className="p-3 rounded-xl bg-white hover:bg-slate-50/80 border border-slate-200/90 hover:border-slate-300 shadow-xs transition-all cursor-pointer group flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <span className="text-[10px] font-mono font-bold text-slate-700 uppercase tracking-wider bg-slate-100 border border-slate-200/80 px-2 py-0.5 rounded leading-none">
                          Periodic Audit
                        </span>
                        <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-700 group-hover:translate-x-0.5 transition-all" />
                      </div>
                      <h3 className="text-xs font-bold text-slate-900 group-hover:text-slate-950 transition-colors">
                        Weekly & Monthly Billing Review
                      </h3>
                      <p className="text-[11px] text-slate-600 mt-1 leading-snug">
                        Review weekly encounter sign-off velocity, monitor billing hold resolution turnaround, and audit monthly revenue.
                      </p>
                    </div>
                    <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-600">
                      <span>Target: Billing Readiness Tab</span>
                      <span className="text-slate-700 group-hover:text-slate-950 font-semibold group-hover:underline">Audit Cycle →</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Embedded Active Billing Holds Work Queue */}
              <div className="pt-2">
                {renderBillingHolds(true)}
              </div>
            </div>

            {/* Slim Horizontal Status Rail directly underneath Billing Command Center */}
            {renderSystemHealth()}

            {/* Operational Expirations Row (Preserves 50 / 50 across workspace, positioned lower on dashboard) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5 sm:gap-4 items-stretch">
              <div className="min-w-0 flex flex-col">
                {renderExpiringAuths(false)}
              </div>
              <div className="min-w-0 flex flex-col">
                {renderExpiringPocs(false)}
              </div>
            </div>
          </div>
        </>
      ) : (
        /* ========================================================================= */
        /* STANDARD / CLINICIAN SURFACE                                              */
        /* ========================================================================= */
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
            {/* Active Clients Card */}
            <div className="mobile-ops-kdi bg-white hover:bg-slate-50/80 border border-slate-200/90 hover:border-emerald-300/80 shadow-xs rounded-xl px-4 py-2.5 relative overflow-hidden transition-all group">
              <div className="relative z-10 flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-700 font-bold">
                <span>Active Clients</span>
                <IconBadge icon={Users} variant="sky" size="xs" />
              </div>
              <div className="relative z-10 flex items-baseline justify-between mt-1">
                <div className="text-2xl font-extrabold text-slate-900 tracking-tight leading-tight">{stats?.activeClients ?? 0}</div>
                <div className="text-[11px] text-emerald-700 flex items-center space-x-1 font-medium">
                  <TrendingUp className="w-3 h-3 shrink-0" />
                  <span>Active Caseload</span>
                </div>
              </div>
            </div>

            {/* Pending Signatures Card */}
            <div className="mobile-ops-kdi bg-white hover:bg-slate-50/80 border border-amber-200/80 hover:border-amber-300 shadow-xs rounded-xl px-4 py-2.5 relative overflow-hidden transition-all group">
              <div className="relative z-10 flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-700 font-bold">
                <span>Pending Signatures</span>
                <IconBadge icon={Clock} variant="amber" size="xs" />
              </div>
              <div className="relative z-10 flex items-baseline justify-between mt-1">
                <div className="text-2xl font-extrabold text-amber-800 tracking-tight leading-tight">{stats?.pendingSignatures ?? 0}</div>
                <div className="text-[11px] text-slate-600 font-medium">Clinician, Guardian & Sup Holds</div>
              </div>
            </div>

            {/* Assigned Caseload Card */}
            <div className="mobile-ops-kdi bg-white hover:bg-slate-50/80 border border-slate-200/90 hover:border-slate-300 shadow-xs rounded-xl px-4 py-2.5 relative overflow-hidden transition-all group">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-700 font-bold">
                <span>Assigned Caseload</span>
                <IconBadge icon={Stethoscope} variant="indigo" size="xs" />
              </div>
              <div className="flex items-baseline justify-between mt-1">
                <div className="text-2xl font-extrabold text-slate-900 tracking-tight leading-tight">Speech & Language</div>
                <div className="text-[11px] text-slate-600 font-medium">Pediatric Visits</div>
              </div>
            </div>
          </div>

          <div className="space-y-3.5 sm:space-y-4">
            {/* Middle Operational Row: 50 / 50 across the workspace */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5 sm:gap-4 items-stretch">
              <div className="min-w-0 flex flex-col">
                {renderExpiringAuths(false)}
              </div>
              <div className="min-w-0 flex flex-col">
                {renderExpiringPocs(false)}
              </div>
            </div>

            {/* Slim Horizontal Status Rail directly underneath Auth + POC panels */}
            {renderSystemHealth()}

            {/* Billing Holds */}
            {renderBillingHolds(false)}
          </div>
        </>
      )}

      {/* Authorization Notice Audit Trail Modal */}
      {selectedAuthHistory && (
        <div
          className="fixed inset-0 bg-slate-950/80 backdrop-blur-md z-50 flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setSelectedAuthHistory(null)}
        >
          <div
            className="bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 border border-slate-800 rounded-2xl max-w-lg w-full p-6 shadow-2xl shadow-black/80 space-y-4 relative overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-sky-500 via-blue-500 to-indigo-500" />
            <div className="flex items-center justify-between pb-4 border-b border-slate-800/80">
              <div className="flex items-center space-x-3">
                <IconBadge icon={Mail} variant="sky" size="md" />
                <div>
                  <h3 className="text-sm font-bold text-white">
                    Notice Audit Trail
                  </h3>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {selectedAuthHistory.auth.client_name} • Auth #{selectedAuthHistory.auth.auth_number || selectedAuthHistory.auth.id}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedAuthHistory(null)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/80 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-2.5 max-h-80 overflow-y-auto pr-1">
              {(() => {
                const history = (selectedAuthHistory.auth.notice_history || []).filter(
                  (n: any) => !selectedAuthHistory.noticeType || n.notice_type === selectedAuthHistory.noticeType
                );

                if (history.length === 0) {
                  return (
                    <div className="py-8 text-center bg-slate-950/40 rounded-xl border border-slate-800/80">
                      <p className="text-xs text-slate-400">
                        No send events recorded yet for this notice type.
                      </p>
                    </div>
                  );
                }

                return history.map((item: any, idx: number) => (
                  <div
                    key={item.id || idx}
                    className="p-3.5 rounded-xl bg-slate-950/60 border border-slate-800/80 text-xs space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                          item.notice_type === '3_DAY'
                            ? 'bg-violet-500/20 text-violet-300 border border-violet-500/30 font-mono'
                            : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-mono'
                        }`}>
                          {item.notice_type === '3_DAY' ? '3-Day Urgent' : '7-Day Notice'}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold uppercase ${
                          item.delivery_status === 'BOUNCED'
                            ? 'bg-rose-600/30 text-rose-300 border border-rose-500/50'
                            : item.delivery_status === 'FAILED'
                            ? 'bg-red-600/30 text-red-300 border border-red-500/50'
                            : item.delivery_status === 'ACCEPTED'
                            ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                            : 'bg-sky-500/20 text-sky-300 border border-sky-500/30'
                        }`}>
                          {item.delivery_status || 'SENT'}
                        </span>
                      </div>
                      <span className="text-[11px] font-mono text-emerald-400 font-semibold">
                        {formatNoticeTimestamp(item.sent_at)}
                      </span>
                    </div>
                    <div className="text-slate-300 text-[11px]">
                      <span>Sender: <strong className="text-white">{item.sender_name || 'Staff'}</strong> ({item.sender_email || 'On file'})</span>
                    </div>
                    <div className="text-slate-400 text-[11px]">
                      <span>To: <strong className="text-slate-200">{item.recipient_emails}</strong></span>
                    </div>
                    {item.delivery_result && (
                      <div className="text-[10px] text-slate-400 font-mono truncate" title={item.delivery_result}>
                        Result: {item.delivery_result}
                      </div>
                    )}
                    {/* Manual reconciliation options if bounce occurred */}
                    <div className="pt-2 flex items-center justify-between border-t border-slate-800/80 mt-1">
                      <span className="text-[10px] text-slate-500">Reconcile status:</span>
                      <div className="flex items-center gap-1.5">
                        {item.delivery_status !== 'BOUNCED' && (
                          <button
                            type="button"
                            disabled={updatingNoticeId === item.id}
                            onClick={() => handleUpdateNoticeStatus(item.id, 'BOUNCED', 'NDR delivery bounce received in info@ mailbox')}
                            className="px-2 py-0.5 text-[9px] font-semibold text-rose-300 hover:text-rose-200 bg-rose-500/10 hover:bg-rose-500/20 rounded-md border border-rose-500/30 transition-colors disabled:opacity-50"
                          >
                            Mark Bounced
                          </button>
                        )}
                        {item.delivery_status !== 'ACCEPTED' && (
                          <button
                            type="button"
                            disabled={updatingNoticeId === item.id}
                            onClick={() => handleUpdateNoticeStatus(item.id, 'ACCEPTED', 'Delivery confirmed / manual reconciliation')}
                            className="px-2 py-0.5 text-[9px] font-semibold text-emerald-300 hover:text-emerald-200 bg-emerald-500/10 hover:bg-emerald-500/20 rounded-md border border-emerald-500/30 transition-colors disabled:opacity-50"
                          >
                            Mark Accepted
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ));
              })()}
            </div>

            <div className="pt-3 border-t border-slate-800/80 flex justify-end">
              <button
                type="button"
                onClick={() => setSelectedAuthHistory(null)}
                className="px-4 py-2 text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl transition-colors border border-slate-700/80"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {resolveServiceId && (
        <BillingHoldResolvePanel
          serviceId={resolveServiceId}
          userRole={userRole}
          onDismiss={() => setResolveServiceId(null)}
          onResolved={() => {
            setResolveServiceId(null);
            onRefreshStats?.();
          }}
          onOpenDocument={(docId) => {
            setResolveServiceId(null);
            onNavigateTab('clinical', docId);
          }}
          onOpenClaims={() => {
            setResolveServiceId(null);
            onNavigateTab('billing', 'claims');
          }}
        />
      )}
    </div>
  );
};
